import {
  FormEvent,
  type InputHTMLAttributes,
  KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  buildSearchProjection,
  cardFromDraft,
  cardSummary,
  cardTypeLabel,
  clearBytes,
  createAccountWalletKeyPackage,
  createDeviceAuthorizationToken,
  createDeviceSecret,
  createPairingKeyPair,
  createPinEnvelope,
  createVaultRootKey,
  decodeBytes,
  deviceKeyFingerprint,
  encodeBytes,
  decryptAttachmentBytes,
  encryptAttachmentVariants,
  fieldValue,
  hashDeviceAuthorizationToken,
  isCollectionRecord,
  isMemoryCard,
  nowIso,
  openSealedVaultRootKey,
  pinEnvelopeNeedsUpgrade,
  unwrapDeviceSecretForHello,
  searchCards,
  sealVaultRootKeyForDevice,
  openAccountWalletKeyPackage,
  unlockVaultRootKey,
  wrapDeviceSecretForHello,
  type AccountWalletKeyPackage,
  type AppSettings,
  type AttachmentRef,
  type CollectionRecord,
  type MaterializedRecord,
  type MemoryCard,
  type PresentationKind,
  type RecordPayload,
  type ReminderSpec,
} from '@safewallet/core';
import { APP_VERSION } from './version';
import { registerOverlayBack, requestOverlayClose, runOverlayBack } from './overlay-back';
import { WalletRecoveryScreen } from './Recovery';
import { RecoveryApprovalInbox, RecoveryInvitationInbox, RecoveryProtectionPanel } from './RecoveryInvitations';
import { connectCloudSync, cloudSyncEnabled, disconnectCloudSync } from './data/cloud-sync';
import { getDatabase } from './data/database';
import { deleteLocalAttachment, deleteLocalAttachments, loadLocalAttachment, saveLocalAttachment, type AttachmentSlot } from './data/attachment-store';
import {
  cancelAttachmentDeletions,
  clearAttachmentSyncContext,
  configureAttachmentSync,
  ensureCloudAttachmentVariant,
  processAttachmentSyncQueue,
  queueAttachmentDeletions,
  r2SyncEnabled,
} from './data/attachment-sync';
import { MAX_PHOTO_INPUT_MB, processPhotoFile } from './data/image-processing';
import {
  MAX_ATTACHMENT_INPUT_MB,
  attachmentPreviewKind,
  isInlineImageMime,
  processNoteAttachmentFile,
} from './data/attachment-processing';
import {
  clearPendingPairing,
  deleteDeviceState,
  loadDeviceState,
  loadDeviceStates,
  loadPendingPairing,
  loadSettings,
  saveDeviceState,
  switchDeviceState,
  savePendingPairing,
  saveSettings,
  type DeviceState,
  type PendingPairingState,
} from './data/device-store';
import { loadDeviceSecret } from './data/device-secret';
import { enrollHello, helloSupported } from './data/hello';
import {
  clearPasswordRecoveryPending,
  cloudConfigured,
  walletRecoveryEnabled,
  currentUserId,
  isPasswordRecoveryPending,
  subscribeToPasswordRecovery,
  supabase,
} from './data/supabase';
import { VaultRepository, type VaultSnapshot } from './data/vault-repository';
import { prepareWalletMerge, type PreparedWalletMerge } from './data/wallet-merge';
import { LOCALES, LanguagePicker, LocaleProvider, detectLocale, isLocale, useLocale, useT, type Locale, type StringKey } from './i18n';

type Screen = 'home' | PresentationKind | 'reminders' | 'collections' | 'settings' | 'pinned' | 'conflicts';
type EditorMode = { kind: Exclude<PresentationKind, 'general'>; item?: MaterializedRecord<MemoryCard>; initialTitle?: string } | null;
type SetupMode = 'choose' | 'create' | 'existing';
type OptionalField = 'website' | 'notes' | 'reminder' | 'collections' | 'securityCode' | 'pin' | 'expiry';

interface HistoryState {
  safeWallet: true;
  screen: Screen;
  layer: 'screen' | 'chooser' | 'viewer' | 'editor' | 'collection-picker' | 'conflict' | 'change-pin';
}

const EMPTY_SNAPSHOT: VaultSnapshot = { cards: [], collections: [], conflictRecords: [] };
const ADD_KINDS: Array<Exclude<PresentationKind, 'general'>> = ['card', 'note', 'password', 'photoId'];
const TYPE_LABELS: Record<Exclude<PresentationKind, 'general'>, string> = {
  note: 'Note',
  password: 'Password',
  card: 'Card',
  photoId: 'Photo ID',
};
const MENU_ENTRIES: Array<{ screen: Screen; labelKey: StringKey; kind?: Exclude<PresentationKind, 'general'> }> = [
  { screen: 'note', labelKey: 'menu.notes', kind: 'note' },
  { screen: 'reminders', labelKey: 'menu.reminders' },
  { screen: 'password', labelKey: 'menu.passwords', kind: 'password' },
  { screen: 'card', labelKey: 'menu.cards', kind: 'card' },
  { screen: 'photoId', labelKey: 'menu.photoIds', kind: 'photoId' },
];
/**
 * A card stores its field names in English inside the encrypted payload, and they are read
 * back by name, so those must never be translated. This maps a stored name to the label the
 * reader sees; anything not listed (an older or custom field) falls back to the stored text.
 */
const FIELD_LABEL_KEYS: Record<string, StringKey> = {
  Title: 'field.title',
  Body: 'field.body',
  Username: 'field.username',
  Password: 'field.password',
  Website: 'field.website',
  Notes: 'field.notes',
  'Card number': 'field.cardNumber',
  'Document number': 'field.documentNumber',
  Expiry: 'field.expiry',
  'Security code': 'field.securityCode',
  PIN: 'field.pin',
  'Document type': 'field.documentType',
  Reminder: 'field.reminder',
  Collections: 'field.collections',
};

const TYPE_SINGULAR_KEYS: Record<Exclude<PresentationKind, 'general'>, StringKey> = {
  note: 'type.note',
  password: 'type.password',
  card: 'type.card',
  photoId: 'type.photoId',
};

// Screen titles use the plural form, which cannot be built by adding "s" in most languages.
const TYPE_PLURAL_KEYS: Record<Exclude<PresentationKind, 'general'>, StringKey> = {
  note: 'type.note.plural',
  password: 'type.password.plural',
  card: 'type.card.plural',
  photoId: 'type.photoId.plural',
};
type Translate = (key: StringKey, vars?: Record<string, string | number>) => string;

/** Falls back to the stored English name for any field this version does not know about. */
function fieldLabel(t: Translate, storedLabel: string): string {
  const key = FIELD_LABEL_KEYS[storedLabel];
  return key ? t(key) : storedLabel;
}

function typeLabel(t: Translate, kind: PresentationKind): string {
  const key = TYPE_SINGULAR_KEYS[kind as Exclude<PresentationKind, 'general'>];
  return key ? t(key) : cardTypeLabel(kind);
}

const PHOTO_ID_TYPES = [
  'Passport',
  "Driver's licence",
  'National ID',
  'Residence permit',
  'Insurance card',
  'Vaccination certificate',
  'Student ID',
  'Other',
];

// Soft PIN throttle: forced waits begin on the 3rd wrong attempt and a recovery
// escalation appears on the 5th. This only slows casual retries at the unlock
// screen; it cannot stop an attacker brute-forcing the local envelope offline.
const PIN_DELAY_AFTER = 3;
const PIN_ESCALATE_AFTER = 5;
function pinRetryDelayMs(attempts: number): number {
  if (attempts < PIN_DELAY_AFTER) return 0;
  if (attempts === 3) return 5_000;
  if (attempts === 4) return 15_000;
  return 30_000;
}


function defaultDeviceName(): string {
  const platform = (navigator as Navigator & { userAgentData?: { platform?: string } }).userAgentData?.platform || navigator.platform || 'Browser';
  return `${platform} browser`;
}

function maskEmail(email?: string): string {
  const value = email?.trim();
  if (!value || !value.includes('@')) return '';
  const [name, domain] = value.split('@');
  const nameFirst = name[0] ?? '';
  const dot = domain.lastIndexOf('.');
  const domainName = dot > 0 ? domain.slice(0, dot) : domain;
  const tld = dot > 0 ? domain.slice(dot) : '';
  const domainFirst = domainName[0] ?? '';
  return `${nameFirst}•••@${domainFirst}•••${tld}`;
}

function cleanWalletLabel(label?: string): string {
  return label?.trim().slice(0, 40) || '';
}

function walletProfileTitle(t: Translate, device: DeviceState): string {
  return cleanWalletLabel(device.walletLabel) || maskEmail(device.accountEmail) || t('wallet.localOnly');
}

function walletProfileSubtitle(t: Translate, device: DeviceState, locale: string): string {
  const updated = new Date(device.updatedAt);
  const lastUsed = Number.isNaN(updated.getTime())
    ? t('wallet.lastUsedRecently')
    : new Intl.DateTimeFormat(locale, { month: 'short', day: 'numeric' }).format(updated);
  const parts = [] as string[];
  const maskedEmail = maskEmail(device.accountEmail);
  if (cleanWalletLabel(device.walletLabel) && maskedEmail) parts.push(maskedEmail);
  if (!device.accountEmail) parts.push(t('wallet.notConnected'));
  parts.push(device.deviceName, lastUsed);
  return parts.join(' · ');
}

function walletOwnerLabel(t: Translate, device: DeviceState): string {
  return maskEmail(device.accountEmail) || cleanWalletLabel(device.walletLabel) || t('wallet.localOnly');
}

function walletDeviceStatus(t: Translate, device: DeviceState): string {
  return device.accountEmail ? t('wallet.availableOnDevice') : t('wallet.onlyThisDevice');
}

function HeaderStatus({ value, saving = false }: { value: string; saving?: boolean }) {
  const [primary, secondary] = value.split(' — ', 2);
  return <div className="header-status"><span className={saving ? 'saving' : ''}>{primary}</span>{secondary && <small>{secondary}</small>}</div>;
}


function accountWalletKeyPackageFromRow(row: Record<string, unknown>): AccountWalletKeyPackage {
  const kdf = row.kdf as AccountWalletKeyPackage['kdf'];
  const nonce = typeof row.nonce === 'string' ? row.nonce : '';
  const ciphertext = typeof row.ciphertext === 'string' ? row.ciphertext : '';
  if (!kdf || kdf.name !== 'argon2id' || !kdf.salt || !nonce || !ciphertext) {
    throw new Error('This wallet needs a one-time upgrade before it can be opened with password + PIN.');
  }
  return {
    version: 1,
    kdf,
    wrappedVaultRootKey: { nonce, ciphertext },
  };
}

function fingerprintWarning(t: Translate, device: DeviceState): string {
  return t(device.accountUserId ? 'fingerprint.confirmBackup' : 'fingerprint.confirmNoBackup');
}

async function verifyLocalPin(device: DeviceState, pin: string): Promise<Uint8Array> {
  const deviceSecret = await loadDeviceSecret(device);
  try {
    return await unlockVaultRootKey(pin, deviceSecret, device.pinEnvelope);
  } finally {
    await clearBytes(deviceSecret);
  }
}

function displayDate(value: string): string {
  const date = new Date(`${value}T12:00:00`);
  return new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: date.getFullYear() === new Date().getFullYear() ? undefined : 'numeric',
  }).format(date);
}

function displayReminder(card: MemoryCard): string {
  if (!card.reminder) return '';
  const date = displayDate(card.reminder.dueDate);
  if (card.reminder.allDay || !card.reminder.localTime) return date;
  const [hours, minutes] = card.reminder.localTime.split(':').map(Number);
  const sample = new Date(2020, 0, 1, hours, minutes);
  return `${date}, ${new Intl.DateTimeFormat(undefined, { hour: '2-digit', minute: '2-digit' }).format(sample)}`;
}

// completedAt is a full timestamp; only the day is shown, as "Done 1 Aug".
function displayCompleted(t: Translate, card: MemoryCard): string {
  const completedAt = card.reminder?.completedAt;
  return completedAt ? t('reminder.doneOn', { date: displayDate(completedAt.slice(0, 10)) }) : '';
}

function reminderTime(card: MemoryCard): number {
  if (!card.reminder) return Number.POSITIVE_INFINITY;
  const time = card.reminder.localTime || '09:00';
  return new Date(`${card.reminder.dueDate}T${time}:00`).getTime();
}

function sixDigitCode(): string {
  const bytes = new Uint32Array(1);
  crypto.getRandomValues(bytes);
  return String(bytes[0] % 1_000_000).padStart(6, '0');
}

function TypeIcon({ type }: { type: PresentationKind }) {
  const common = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  if (type === 'password') return <svg {...common}><rect x="4" y="10" width="16" height="10" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/><circle cx="12" cy="15" r="1"/></svg>;
  if (type === 'card') return <svg {...common}><rect x="3" y="5" width="18" height="14" rx="2"/><path d="M3 9h18M7 15h4"/></svg>;
  if (type === 'photoId') return <svg {...common}><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="9" cy="10" r="2"/><path d="M5.5 16a4 4 0 0 1 7 0M15 9h3M15 13h3"/></svg>;
  return <svg {...common}><path d="M6 3h9l3 3v15H6z"/><path d="M15 3v4h4M9 12h6M9 16h6"/></svg>;
}

function HomeIcon() {
  return <svg width="21" height="21" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="m3 10 9-7 9 7"/><path d="M5 9.5V21h14V9.5"/><path d="M9 21v-7h6v7"/></svg>;
}


function EyeIcon({ revealed }: { revealed: boolean }) {
  const common = {
    width: 20,
    height: 20,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.7,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
  };
  return revealed
    ? <svg {...common}><path d="M2.5 12s3.5-6 9.5-6 9.5 6 9.5 6-3.5 6-9.5 6-9.5-6-9.5-6Z"/><circle cx="12" cy="12" r="2.5"/></svg>
    : <svg {...common}><path d="M3 3l18 18M10.6 6.2A10.8 10.8 0 0 1 12 6c6 0 9.5 6 9.5 6a16 16 0 0 1-3 3.7M6.2 6.2C3.9 8 2.5 12 2.5 12s3.5 6 9.5 6c1.5 0 2.9-.4 4.1-1M9.9 9.9a3 3 0 0 0 4.2 4.2"/></svg>;
}

function SensitiveInput({ className = '', ...props }: Omit<InputHTMLAttributes<HTMLInputElement>, 'type'>) {
  const [revealed, setRevealed] = useState(false);
  return (
    <span className="sensitive-input">
      <input
        {...props}
        type={revealed ? 'text' : 'password'}
        autoComplete={props.autoComplete ?? 'off'}
        autoCapitalize="none"
        autoCorrect="off"
        spellCheck={false}
        data-form-type="other"
        data-lpignore="true"
        data-1p-ignore="true"
        data-bwignore="true"
        className={className}
      />
      <button type="button" onClick={() => setRevealed((value) => !value)} aria-label={revealed ? 'Hide value' : 'Show value'} title={revealed ? 'Hide' : 'Show'}>
        <EyeIcon revealed={revealed} />
      </button>
    </span>
  );
}

function GateShell({ children }: { children: React.ReactNode }) {
  return <main className="gate-shell"><section className="gate-card">{children}<div className="gate-footer"><LanguagePicker /><p className="app-version">Version {APP_VERSION}</p></div></section></main>;
}

async function sendAccountPasswordReset(email: string): Promise<void> {
  if (!supabase) throw new Error('Account synchronization is not configured.');
  const normalizedEmail = email.trim();
  if (!normalizedEmail) throw new Error('Enter your account email first.');
  const { error } = await supabase.auth.resetPasswordForEmail(normalizedEmail, {
    redirectTo: `${window.location.origin}/?password-recovery=1`,
  });
  if (error) throw error;
}

function PasswordRecoveryScreen({ onDone }: { onDone: () => void }) {
  const { t } = useT();
  const [password, setPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [complete, setComplete] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!supabase) return;
    let cancelled = false;
    void supabase.auth.getSession().then(({ data }) => {
      if (!cancelled) setReady(Boolean(data.session));
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY' || (event === 'SIGNED_IN' && session)) setReady(true);
    });
    return () => {
      cancelled = true;
      subscription.unsubscribe();
    };
  }, []);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!supabase) return;
    if (password.length < 8) return setError(t('recover.errShort'));
    if (password !== confirmation) return setError(t('recover.errMismatch'));
    setBusy(true);
    setError('');
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password });
      if (updateError) throw updateError;
      await supabase.auth.signOut();
      clearPasswordRecoveryPending();
      setComplete(true);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('recover.errUpdate'));
    } finally {
      setBusy(false);
    }
  }

  if (complete) {
    return (
      <GateShell>
        <p className="brand">{t('recover.accountTitle')}</p>
        <h1>{t('recover.passwordChanged')}</h1>
        <p className="gate-copy">{t('recover.passwordChangedNote')}</p>
        <button className="primary-button" type="button" onClick={onDone}>{t('recover.continue')}</button>
      </GateShell>
    );
  }

  return (
    <GateShell>
      <p className="brand">{t('recover.accountTitle')}</p>
      <h1>{t('recover.choosePassword')}</h1>
      <p className="gate-copy">{t('recover.choosePasswordNote')}</p>
      {!ready ? <p className="status-message">{t('recover.checkingLink')}</p> : <form className="gate-form" autoComplete="off" onSubmit={submit}>
        <label>{t('recover.newPassword')}<SensitiveInput autoFocus value={password} onChange={(event) => setPassword(event.target.value)} /></label>
        <label>{t('recover.repeatPassword')}<SensitiveInput value={confirmation} onChange={(event) => setConfirmation(event.target.value)} /></label>
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="primary-button" disabled={busy} type="submit">{busy ? t('recover.updating') : t('recover.updatePassword')}</button>
      </form>}
    </GateShell>
  );
}

function InitialSetup({ onReady, onRecover, initialMode, initialEmail }: { onReady: (state: DeviceState, key: Uint8Array) => Promise<void>; onRecover: () => void; initialMode?: SetupMode; initialEmail?: string }) {
  const [mode, setMode] = useState<SetupMode>(initialMode ?? 'choose');
  const { t } = useT();
  if (mode === 'create') return <CreateWallet onBack={() => setMode('choose')} onReady={onReady} />;
  if (mode === 'existing') return <ExistingWalletSetup onBack={() => setMode('choose')} onReady={onReady} onRecover={onRecover} initialEmail={initialEmail} />;
  return (
    <GateShell>
      <p className="brand">Safe Wallet</p>
      <h1>{t('gate.title')}</h1>
      <p className="gate-copy">{t('gate.copy')}</p>
      <div className="gate-actions">
        <button className="primary-button" type="button" onClick={() => setMode('create')}>{t('gate.create')}</button>
        <button className="secondary-button" type="button" disabled={!cloudConfigured} onClick={() => setMode('existing')}>{t('gate.open')}</button>
        {walletRecoveryEnabled && <button className="quiet-link" type="button" onClick={onRecover}>{t('gate.recover')}</button>}
      </div>
      {!cloudConfigured && <p className="quiet-note">{t('gate.cloudNotConfigured')}</p>}
    </GateShell>
  );
}

function CreateWallet({ onBack, onReady }: { onBack: () => void; onReady: (state: DeviceState, key: Uint8Array) => Promise<void> }) {
  const { t, locale } = useT();
  const [pin, setPin] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [deviceName, setDeviceName] = useState(defaultDeviceName());
  const [walletLabel, setWalletLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [helloStep, setHelloStep] = useState(false);
  const pendingRef = useRef<{ state: DeviceState; key: Uint8Array; secret: Uint8Array } | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    if (!/^\d{6,12}$/.test(pin)) return setError(t('create.errPin'));
    if (pin !== confirmation) return setError(t('create.errMismatch'));
    setBusy(true);
    let secret: Uint8Array | null = null;
    try {
      const key = await createVaultRootKey();
      secret = await createDeviceSecret();
      const deviceAuthToken = await createDeviceAuthorizationToken();
      const envelope = await createPinEnvelope(pin, secret, key);
      const timestamp = nowIso();
      const state: DeviceState = {
        version: 1,
        deviceId: crypto.randomUUID(),
        deviceName: deviceName.trim() || defaultDeviceName(),
        walletLabel: cleanWalletLabel(walletLabel) || undefined,
        vaultId: crypto.randomUUID(),
        deviceSecret: encodeBytes(secret),
        deviceAuthToken,
        pinEnvelope: envelope,
        createdAt: timestamp,
        updatedAt: timestamp,
      };
      await saveDeviceState(state);
      // On a device that can do Windows Hello, offer to turn it on before entering.
      if (await helloSupported()) {
        pendingRef.current = { state, key, secret };
        secret = null; // handed to the Hello step; must not be cleared here
        setHelloStep(true);
        return;
      }
      await onReady(state, key);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('create.errFailed'));
    } finally {
      await clearBytes(secret);
      setBusy(false);
    }
  }

  async function enterWith(next: DeviceState, key: Uint8Array, secret: Uint8Array) {
    pendingRef.current = null;
    await clearBytes(secret);
    await onReady(next, key);
  }

  async function enableHello() {
    const pending = pendingRef.current;
    if (!pending) return;
    setBusy(true);
    setError('');
    try {
      if (!confirm(fingerprintWarning(t, pending.state))) {
        setBusy(false);
        return;
      }
      const enrollment = await enrollHello(pending.state.walletLabel || pending.state.deviceName);
      const wrappedDeviceSecret = await wrapDeviceSecretForHello(pending.secret, enrollment.hardwareSecret);
      await clearBytes(enrollment.hardwareSecret);
      // enrollHello proved the read path works, so dropping the plaintext copy is safe.
      // Without it, a copy of local storage cannot be used to guess the PIN offline.
      const updated: DeviceState = {
        ...pending.state,
        deviceSecret: undefined,
        helloCredentialId: enrollment.credentialId,
        helloPrfSalt: enrollment.prfSalt,
        wrappedDeviceSecret,
        updatedAt: nowIso(),
      };
      await saveDeviceState(updated);
      await enterWith(updated, pending.key, pending.secret);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('create.errHello'));
      setBusy(false);
    }
  }

  async function skipHello() {
    const pending = pendingRef.current;
    if (!pending) return;
    setBusy(true);
    await enterWith(pending.state, pending.key, pending.secret);
  }

  if (helloStep) {
    return (
      <GateShell>
        <p className="brand">Safe Wallet</p>
        <h1>{t('create.fingerprintTitle')}</h1>
        <p className="gate-copy">{t('create.fingerprintCopy')}</p>
        {error && <p className="form-error" role="alert">{error}</p>}
        <div className="gate-actions">
          <button className="primary-button" disabled={busy} type="button" onClick={() => void enableHello()}>{busy ? t('fingerprint.settingUp') : t('create.turnOn')}</button>
          <button className="secondary-button" disabled={busy} type="button" onClick={() => void skipHello()}>{t('create.notNow')}</button>
        </div>
      </GateShell>
    );
  }

  return (
    <GateShell>
      <button className="quiet-link" type="button" onClick={onBack}>Back</button>
      <p className="brand">Safe Wallet</p>
      <h1>{t('create.title')}</h1>
      <p className="gate-copy">{t('create.intro')}</p>
      <form className="gate-form" autoComplete="off" onSubmit={submit}>
        <label>{t('create.walletName')} <span className="optional-label">optional</span><input value={walletLabel} onChange={(event) => setWalletLabel(event.target.value.slice(0, 40))} autoComplete="off" placeholder={t('create.walletNamePlaceholder')} /><small className="field-hint">{t('create.walletNameHint')}</small></label>
        <label>{t('create.deviceName')}<input value={deviceName} onChange={(event) => setDeviceName(event.target.value)} autoComplete="off" /></label>
        <label>{t('create.appPin')}<SensitiveInput autoFocus inputMode="numeric" value={pin} onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 12))} placeholder={t('account.pinPlaceholder')} /></label>
        <label>{t('create.repeatPin')}<SensitiveInput inputMode="numeric" value={confirmation} onChange={(event) => setConfirmation(event.target.value.replace(/\D/g, '').slice(0, 12))} /></label>
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="primary-button" disabled={busy} type="submit">{busy ? t('create.creating') : t('create.createWallet')}</button>
      </form>
    </GateShell>
  );
}

function ExistingWalletSetup({ onBack, onReady, onRecover, initialEmail }: { onBack: () => void; onReady: (state: DeviceState, key: Uint8Array) => Promise<void>; onRecover: () => void; initialEmail?: string }) {
  const { t } = useT();
  const [email, setEmail] = useState(initialEmail ?? '');
  const [accountPassword, setAccountPassword] = useState('');
  const [safeWalletPin, setSafeWalletPin] = useState('');
  const [deviceName, setDeviceName] = useState(defaultDeviceName());
  const [pairing, setPairing] = useState<PendingPairingState | null>(null);
  const [pairingFingerprint, setPairingFingerprint] = useState('');
  const [pairingFingerprintError, setPairingFingerprintError] = useState('');
  const [wrappedKey, setWrappedKey] = useState<string | null>(null);
  const [pin, setPin] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    void loadPendingPairing().then(setPairing);
  }, []);

  useEffect(() => {
    let cancelled = false;
    setPairingFingerprint('');
    setPairingFingerprintError('');
    if (!pairing) return () => { cancelled = true; };
    void deviceKeyFingerprint(pairing.publicKey)
      .then((value) => { if (!cancelled) setPairingFingerprint(value); })
      .catch(() => { if (!cancelled) setPairingFingerprintError(t('existing.errPairingInvalid')); });
    return () => { cancelled = true; };
  }, [pairing]);

  useEffect(() => {
    if (!pairing || wrappedKey || !supabase) return;
    const client = supabase;
    const timer = window.setInterval(async () => {
      const { data } = await client
        .from('device_pairing_requests')
        .select('state, wrapped_vrk, expires_at')
        .eq('id', pairing.requestId)
        .maybeSingle();
      if ((data?.state === 'approved' || data?.state === 'completed') && data.wrapped_vrk) setWrappedKey(String(data.wrapped_vrk));
      if (data?.state === 'cancelled') {
        setError(t('existing.errCancelled'));
        setPairing(null);
        void clearPendingPairing();
      } else if (data?.state === 'expired' || (data?.expires_at && new Date(data.expires_at).getTime() < Date.now())) {
        setError(t('existing.errExpired'));
        setPairing(null);
        void clearPendingPairing();
      }
    }, 2500);
    return () => window.clearInterval(timer);
  }, [pairing, wrappedKey]);

  async function openWithPasswordAndPin(event: FormEvent) {
    event.preventDefault();
    if (!supabase) return;
    if (!/^\d{6,12}$/.test(safeWalletPin)) return setError(t('existing.errPin'));
    setBusy(true);
    setError('');
    setMessage('');
    let secret: Uint8Array | null = null;
    let key: Uint8Array | null = null;
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password: accountPassword });
      if (signInError) throw signInError;

      const { data: vaultId, error: vaultError } = await supabase.rpc('my_vault_id');
      if (vaultError) throw vaultError;
      if (!vaultId) throw new Error(t('existing.errNoWallet'));

      const { data: packageRow, error: packageError } = await supabase.rpc('my_vault_key_package');
      if (packageError) throw packageError;
      const packageRecord = Array.isArray(packageRow) ? packageRow[0] : packageRow;
      if (!packageRecord) {
        throw new Error(t('existing.errUpgrade'));
      }

      key = await openAccountWalletKeyPackage(
        accountPassword,
        safeWalletPin,
        accountWalletKeyPackageFromRow(packageRecord as Record<string, unknown>),
        String(vaultId),
      );

      secret = await createDeviceSecret();
      const deviceAuthToken = await createDeviceAuthorizationToken();
      const deviceAuthTokenHash = await hashDeviceAuthorizationToken(deviceAuthToken);
      const deviceId = crypto.randomUUID();
      const name = deviceName.trim() || defaultDeviceName();
      const envelope = await createPinEnvelope(safeWalletPin, secret, key);
      const { error: registerError } = await supabase.rpc('register_wallet_device_from_key_package', {
        p_device_id: deviceId,
        p_device_name: name,
        p_device_auth_token_hash: deviceAuthTokenHash,
      });
      if (registerError) throw registerError;

      const userId = await currentUserId();
      const { data: userData } = await supabase.auth.getUser();
      const accountEmail = userData.user?.email ?? email.trim();
      const timestamp = nowIso();
      const state: DeviceState = {
        version: 1,
        deviceId,
        deviceName: name,
        vaultId: String(vaultId),
        deviceSecret: encodeBytes(secret),
        deviceAuthToken,
        pinEnvelope: envelope,
        accountUserId: userId ?? undefined,
        accountEmail: accountEmail || undefined,
        createdAt: timestamp,
        updatedAt: timestamp,
      };
      await saveDeviceState(state);
      await clearPendingPairing();
      await onReady(state, key);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('unlock.errFailed'));
    } finally {
      await clearBytes(secret);
      setBusy(false);
    }
  }

  async function signInAndRequest(event: FormEvent) {
    event.preventDefault();
    if (!supabase) return;
    setBusy(true);
    setError('');
    setMessage('');
    try {
      const { error: signInError } = await supabase.auth.signInWithPassword({ email, password: accountPassword });
      if (signInError) throw signInError;
      const { data: vaultId, error: vaultError } = await supabase.rpc('my_vault_id');
      if (vaultError) throw vaultError;
      if (!vaultId) throw new Error(t('existing.errNoWallet'));
      const keys = await createPairingKeyPair();
      const request: PendingPairingState = {
        requestId: crypto.randomUUID(),
        deviceId: crypto.randomUUID(),
        deviceName: deviceName.trim() || defaultDeviceName(),
        displayCode: sixDigitCode(),
        publicKey: keys.publicKey,
        privateKey: keys.privateKey,
        vaultId: String(vaultId),
        expiresAt: new Date(Date.now() + 10 * 60_000).toISOString(),
      };
      const { error: requestError } = await supabase.rpc('request_device_pairing', {
        p_request_id: request.requestId,
        p_device_id: request.deviceId,
        p_device_name: request.deviceName,
        p_public_key: request.publicKey,
        p_display_code: request.displayCode,
        p_expires_at: request.expiresAt,
      });
      if (requestError) throw requestError;
      await savePendingPairing(request);
      setPairing(request);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('existing.errRequest'));
    } finally {
      setBusy(false);
    }
  }

  async function resetAccountPassword() {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await sendAccountPasswordReset(email);
      setMessage(t('account.resetSent'));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('account.errReset'));
    } finally {
      setBusy(false);
    }
  }

  async function cancelPairing() {
    const current = pairing;
    setPairing(null);
    setWrappedKey(null);
    await clearPendingPairing();
    if (!current || !supabase) return;
    const { error: cancelError } = await supabase.rpc('cancel_device_pairing', {
      p_request_id: current.requestId,
    });
    if (cancelError) console.warn(t('existing.errCancel'), cancelError);
  }

  async function finish(event: FormEvent) {
    event.preventDefault();
    if (!pairing || !wrappedKey || !supabase) return;
    if (!/^\d{6,12}$/.test(pin)) return setError(t('create.errPin'));
    if (pin !== confirmation) return setError(t('create.errMismatch'));
    setBusy(true);
    let secret: Uint8Array | null = null;
    try {
      const key = await openSealedVaultRootKey(wrappedKey, pairing.publicKey, pairing.privateKey);
      secret = await createDeviceSecret();
      const deviceAuthToken = await createDeviceAuthorizationToken();
      const deviceAuthTokenHash = await hashDeviceAuthorizationToken(deviceAuthToken);
      const envelope = await createPinEnvelope(pin, secret, key);
      const userId = await currentUserId();
      const { data: userData } = await supabase.auth.getUser();
      const accountEmail = userData.user?.email ?? email.trim();
      const timestamp = nowIso();
      const state: DeviceState = {
        version: 1,
        deviceId: pairing.deviceId,
        deviceName: pairing.deviceName,
        vaultId: pairing.vaultId,
        deviceSecret: encodeBytes(secret),
        deviceAuthToken,
        pinEnvelope: envelope,
        accountUserId: userId ?? undefined,
        accountEmail: accountEmail || undefined,
        createdAt: timestamp,
        updatedAt: timestamp,
      };
      const { error: completeError } = await supabase.rpc('complete_device_pairing', {
        p_request_id: pairing.requestId,
        p_device_id: pairing.deviceId,
        p_device_name: pairing.deviceName,
        p_public_key: pairing.publicKey,
        p_device_auth_token_hash: deviceAuthTokenHash,
      });
      if (completeError) throw completeError;
      if (accountPassword) {
        const keyPackage = await createAccountWalletKeyPackage(accountPassword, pin, key, pairing.vaultId);
        const { error: packageError } = await supabase.rpc('upsert_vault_key_package', {
          p_vault_id: pairing.vaultId,
          p_kdf: keyPackage.kdf,
          p_nonce: keyPackage.wrappedVaultRootKey.nonce,
          p_ciphertext: keyPackage.wrappedVaultRootKey.ciphertext,
        });
        if (packageError) throw packageError;
      }
      await saveDeviceState(state);
      await clearPendingPairing();
      await onReady(state, key);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('existing.errFinish'));
    } finally {
      await clearBytes(secret);
      setBusy(false);
    }
  }

  if (pairing && !wrappedKey) {
    return (
      <GateShell>
        <button className="quiet-link" type="button" onClick={onBack}>Back</button>
        <p className="brand">{t('existing.approvalTitle')}</p>
        <p className="eyebrow">{t('pairing.fingerprint')}</p>
        <h1 className="pairing-fingerprint">{pairingFingerprint || (pairingFingerprintError ? t('existing.unavailable') : t('existing.calculating'))}</h1>
        <p className="gate-copy">On an unlocked Safe Wallet device, open Settings → Account &amp; devices and review “{pairing.deviceName}”. Compare this fingerprint exactly before approving.</p>
        <p className="pairing-warning">{t('existing.mismatchNote')}</p>
        {pairingFingerprintError && <p className="form-error" role="alert">{pairingFingerprintError}</p>}
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="secondary-button" type="button" onClick={() => void cancelPairing()}>{t('existing.cancelRequest')}</button>
      </GateShell>
    );
  }

  if (pairing && wrappedKey) {
    return (
      <GateShell>
        <p className="brand">{t('existing.approved')}</p>
        <h1>{t('existing.setPin')}</h1>
        <p className="gate-copy">{t('existing.setPinNote')}</p>
        <form className="gate-form" autoComplete="off" onSubmit={finish}>
          <label>{t('create.appPin')}<SensitiveInput autoFocus inputMode="numeric" value={pin} onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 12))} /></label>
          <label>{t('create.repeatPin')}<SensitiveInput inputMode="numeric" value={confirmation} onChange={(event) => setConfirmation(event.target.value.replace(/\D/g, '').slice(0, 12))} /></label>
          {error && <p className="form-error" role="alert">{error}</p>}
          <button className="primary-button" disabled={busy} type="submit">{busy ? t('existing.finishing') : t('existing.openWallet')}</button>
        </form>
      </GateShell>
    );
  }

  return (
    <GateShell>
      <button className="quiet-link" type="button" onClick={onBack}>Back</button>
      <p className="brand">{t('existing.title')}</p>
      <h1>{t('existing.heading')}</h1>
      <p className="gate-copy">{t('existing.intro')}</p>
      <form className="gate-form" autoComplete="off" onSubmit={openWithPasswordAndPin}>
        <label>{t('account.email')}<input type="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={email} onChange={(event) => setEmail(event.target.value)} /></label>
        <label>{t('pin.accountPassword')}<SensitiveInput value={accountPassword} onChange={(event) => setAccountPassword(event.target.value)} /></label>
        <label>{t('account.pinLabel')}<SensitiveInput inputMode="numeric" value={safeWalletPin} onChange={(event) => setSafeWalletPin(event.target.value.replace(/\D/g, '').slice(0, 12))} placeholder={t('account.pinPlaceholder')} /></label>
        <label>{t('create.deviceName')}<input value={deviceName} onChange={(event) => setDeviceName(event.target.value)} /></label>
        <button className="quiet-link form-link" disabled={busy || !email.trim()} type="button" onClick={() => void resetAccountPassword()}>{t('account.forgotPassword')}</button>
        {walletRecoveryEnabled && <button className="quiet-link form-link" disabled={busy} type="button" onClick={onRecover}>{t('existing.forgotPin')}</button>}
        {message && <p className="status-message" role="status">{message}</p>}
        {error && <p className="form-error" role="alert">{error}</p>}
        <button className="primary-button" disabled={busy || !email || !accountPassword || !safeWalletPin} type="submit">{busy ? 'Opening…' : 'Open wallet'}</button>
      </form>
      <details className="advanced-device-approval gate-advanced">
        <summary>{t('existing.advanced')}</summary>
        <p className="field-hint">{t('existing.advancedNote')}</p>
        <form className="gate-form" autoComplete="off" onSubmit={signInAndRequest}>
          <label>{t('account.email')}<input type="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={email} onChange={(event) => setEmail(event.target.value)} /></label>
          <label>{t('pin.accountPassword')}<SensitiveInput value={accountPassword} onChange={(event) => setAccountPassword(event.target.value)} /></label>
          <label>{t('create.deviceName')}<input value={deviceName} onChange={(event) => setDeviceName(event.target.value)} /></label>
          <button className="primary-button" disabled={busy || !email || !accountPassword} type="submit">{busy ? t('existing.requesting') : t('existing.requestApproval')}</button>
        </form>
      </details>
    </GateShell>
  );
}

function UnlockScreen({ device, walletCount, onUnlock, onRecover, onUseAnother }: {
  device: DeviceState;
  walletCount: number;
  onUnlock: (pin: string) => Promise<void>;
  onRecover: () => void;
  onUseAnother: () => void;
}) {
  const { t, locale } = useT();
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [now, setNow] = useState(() => Date.now());

  const attempts = device.failedUnlocks ?? 0;
  const retryAt = device.retryAfter ? new Date(device.retryAfter).getTime() : 0;
  const waitMs = Math.max(0, retryAt - now);
  const waiting = waitMs > 0;
  const waitSeconds = Math.ceil(waitMs / 1000);
  const escalated = attempts >= PIN_ESCALATE_AFTER;

  useEffect(() => {
    if (retryAt <= Date.now()) return;
    const timer = window.setInterval(() => {
      setNow(Date.now());
      if (Date.now() >= retryAt) window.clearInterval(timer);
    }, 250);
    return () => window.clearInterval(timer);
  }, [retryAt]);

  const fingerprintReady = Boolean(device.helloCredentialId && device.helloPrfSalt && device.wrappedDeviceSecret);

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (waiting) return;
    setBusy(true);
    setError('');
    try {
      await onUnlock(pin);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('unlock.errFailed'));
      setPin('');
    } finally {
      setBusy(false);
    }
  }
  return (
    <GateShell>
      <p className="brand">Safe Wallet</p>
      <h1>{t('unlock.title')}</h1>
      <div className="unlock-profile-card">
        <strong>{walletProfileTitle(t, device)}</strong>
        <small>{walletProfileSubtitle(t, device, locale)}</small>
      </div>
      {escalated && (
        <div className="unlock-escalation" role="alert">
          <strong>{t('unlock.tooMany')}</strong>
          <p>{device.accountUserId
            ? t('unlock.tooManyBackup')
            : t('unlock.tooManyLocal')}</p>
        </div>
      )}
      <form className="gate-form" autoComplete="off" onSubmit={submit}>
        <label>{t('account.pinLabel')}<SensitiveInput autoFocus inputMode="numeric" value={pin} onChange={(event) => setPin(event.target.value.replace(/\D/g, '').slice(0, 12))} /></label>
        {error && <p className="form-error" role="alert">{error}</p>}
        {waiting && <p className="status-message" role="status">{t('unlock.tooManyAttempts', { seconds: waitSeconds })}</p>}
        <button className="primary-button" disabled={busy || waiting || pin.length < 6} type="submit">{busy ? t('unlock.opening') : waiting ? t('unlock.wait', { seconds: waitSeconds }) : fingerprintReady ? t('unlock.withFingerprint') : t('unlock.unlock')}</button>
        <button className="quiet-link form-link" disabled={busy} type="button" onClick={onUseAnother}>{walletCount > 1 ? t('settings.chooseAnotherWallet') : t('settings.useAnotherAccount')}</button>
        {walletRecoveryEnabled && <button className="quiet-link form-link" disabled={busy} type="button" onClick={onRecover}>{t('unlock.forgotPin')}</button>}
      </form>
    </GateShell>
  );
}


function WalletChooser({ profiles, currentVaultId, showBack, onSelect, onAdd, onBack }: {
  profiles: DeviceState[];
  currentVaultId?: string;
  showBack: boolean;
  onSelect: (device: DeviceState) => void;
  onAdd: () => void;
  onBack: () => void;
}) {
  const { t, locale } = useT();
  return (
    <GateShell>
      {showBack && <button className="quiet-link" type="button" onClick={onBack}>Back</button>}
      <p className="brand">Safe Wallet</p>
      <h1>{t('chooser.chooseWallet')}</h1>
      <p className="gate-copy">{t('chooser.eachHasPin')}</p>
      <div className="profile-list">
        {profiles.map((profile) => (
          <button className="profile-card" key={profile.vaultId} type="button" onClick={() => onSelect(profile)}>
            <span><strong>{walletProfileTitle(t, profile)}</strong><small>{walletProfileSubtitle(t, profile, locale)}</small></span>
            {profile.vaultId === currentVaultId && <em>{t('chooser.current')}</em>}
          </button>
        ))}
      </div>
      <button className="secondary-button" type="button" onClick={onAdd}>{t('chooser.addAnother')}</button>
    </GateShell>
  );
}

function ChangePinDialog({ onChange, onClose, requireAccountPassword }: {
  onChange: (currentPin: string, newPin: string, accountPassword: string) => Promise<void>;
  onClose: () => void;
  requireAccountPassword: boolean;
}) {
  const { t } = useT();
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [accountPassword, setAccountPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    if (!/^\d{6,12}$/.test(currentPin)) return setError(t('pin.errCurrent'));
    if (!/^\d{6,12}$/.test(newPin)) return setError(t('pin.errNew'));
    if (newPin !== confirmation) return setError(t('pin.errMismatch'));
    if (newPin === currentPin) return setError(t('pin.errSame'));
    if (requireAccountPassword && !accountPassword) return setError(t('pin.errPassword'));

    setBusy(true);
    try {
      await onChange(currentPin, newPin, accountPassword);
      onClose();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('pin.errFailed'));
    } finally {
      setBusy(false);
    }
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLFormElement>) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      event.currentTarget.requestSubmit();
    }
  }

  return (
    <div className="overlay" role="presentation" onMouseDown={onClose}>
      <section className="sheet pin-change-sheet" role="dialog" aria-modal="true" aria-labelledby="change-pin-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-header"><button className="text-button" type="button" onClick={onClose}>{t('action.cancel')}</button><h2 id="change-pin-title">{t('pin.changeTitle')}</h2><span className="sheet-header-spacer" /></div>
        <form className="pin-change-form" autoComplete="off" onSubmit={submit} onKeyDown={handleKeyDown}>
          <p>{requireAccountPassword
            ? t('pin.noteSynced')
            : t('pin.noteLocal')}</p>
          <label>{t('pin.current')}<SensitiveInput autoFocus inputMode="numeric" value={currentPin} onChange={(event) => setCurrentPin(event.target.value.replace(/\D/g, '').slice(0, 12))} /></label>
          <label>{t('pin.new')}<SensitiveInput inputMode="numeric" value={newPin} onChange={(event) => setNewPin(event.target.value.replace(/\D/g, '').slice(0, 12))} /></label>
          <label>{t('pin.repeat')}<SensitiveInput inputMode="numeric" value={confirmation} onChange={(event) => setConfirmation(event.target.value.replace(/\D/g, '').slice(0, 12))} /></label>
          {requireAccountPassword && <label>{t('pin.accountPassword')}<SensitiveInput value={accountPassword} onChange={(event) => setAccountPassword(event.target.value)} /><small className="field-hint">{t('pin.accountPasswordHint')}</small></label>}
          {error && <p className="form-error" role="alert">{error}</p>}
          <div className="pin-change-actions"><button className="secondary-button" type="button" onClick={onClose}>{t('action.cancel')}</button><button className="primary-button" disabled={busy || currentPin.length < 6 || newPin.length < 6 || confirmation.length < 6 || (requireAccountPassword && !accountPassword)} type="submit">{busy ? t('pin.changing') : t('pin.change')}</button></div>
        </form>
      </section>
    </div>
  );
}

function AddChooser({ onChoose, onClose }: { onChoose: (kind: Exclude<PresentationKind, 'general'>) => void; onClose: () => void }) {
  const { t } = useT();
  return (
    <div className="overlay" role="presentation" onMouseDown={onClose}>
      <section className="sheet chooser" role="dialog" aria-modal="true" aria-labelledby="chooser-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-header"><button className="text-button" type="button" onClick={onClose}>{t('action.cancel')}</button><h2 id="chooser-title">{t('chooser.addNew')}</h2><span className="sheet-header-spacer" /></div>
        <div className="chooser-list">
          {ADD_KINDS.map((kind) => <button key={kind} type="button" onClick={() => onChoose(kind)}><TypeIcon type={kind}/><span>{typeLabel(t, kind)}</span></button>)}
        </div>
      </section>
    </div>
  );
}

function CollectionPicker({ collections, selected, onSave, onClose }: {
  collections: Array<MaterializedRecord<CollectionRecord>>;
  selected: string[];
  onSave: (ids: string[]) => void;
  onClose: () => void;
}) {
  const { t } = useT();
  const [value, setValue] = useState(new Set(selected));
  return (
    <div className="overlay" role="presentation" onMouseDown={onClose}>
      <section className="sheet collection-picker" role="dialog" aria-modal="true" aria-labelledby="collection-picker-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-header"><h2 id="collection-picker-title">{t('field.collections')}</h2><button className="text-button" type="button" onClick={() => onSave([...value])}>{t('action.save')}</button></div>
        {collections.length === 0 && <p className="small-empty">{t('collections.createFirst')}</p>}
        <div className="check-list">
          {collections.map(({ payload }) => (
            <label key={payload.recordId} className="check-row">
              <input type="checkbox" checked={value.has(payload.recordId)} onChange={() => setValue((current) => {
                const next = new Set(current);
                if (next.has(payload.recordId)) next.delete(payload.recordId); else next.add(payload.recordId);
                return next;
              })}/>
              <span>{payload.name}</span>
            </label>
          ))}
        </div>
      </section>
    </div>
  );
}

function OptionalFieldButton({ label, onClick }: { label: string; onClick: () => void }) {
  return <button className="optional-field-button" type="button" onClick={onClick}>+ {label}</button>;
}

const EXPIRY_MONTH_LABELS = Array.from({ length: 12 }, (_, month) =>
  new Intl.DateTimeFormat(undefined, { month: 'short' }).format(new Date(2020, month, 1)),
);

function parseExpiry(value: string): { year: number; month: number } | null {
  const isoMatch = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(value.trim());
  if (isoMatch) return { year: Number(isoMatch[1]), month: Number(isoMatch[2]) };

  const shortMatch = /^(0?[1-9]|1[0-2])\s*[\/. -]\s*(\d{2}|\d{4})$/.exec(value.trim());
  if (!shortMatch) return null;
  const shortYear = Number(shortMatch[2]);
  return { year: shortYear < 100 ? 2000 + shortYear : shortYear, month: Number(shortMatch[1]) };
}

function formatExpiry(value: string): string {
  const parsed = parseExpiry(value);
  return parsed ? `${String(parsed.month).padStart(2, '0')} / ${String(parsed.year).slice(-2)}` : 'MM / YY';
}

function ExpiryField({ value, onChange, compact = false }: { value: string; onChange: (value: string) => void; compact?: boolean }) {
  const { t } = useT();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLElement>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const selected = parseExpiry(value);
  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const minYear = Math.min(currentYear - 20, selected?.year ?? currentYear);
  const maxYear = Math.max(currentYear + 30, selected?.year ?? currentYear);
  const [visibleYear, setVisibleYear] = useState(selected?.year ?? currentYear);

  useEffect(() => {
    if (!pickerOpen) return;
    const focusTarget = dialogRef.current?.querySelector<HTMLElement>('.expiry-month.selected')
      ?? dialogRef.current?.querySelector<HTMLElement>('.expiry-month.current')
      ?? dialogRef.current?.querySelector<HTMLElement>('button:not(:disabled)');
    focusTarget?.focus();
  }, [pickerOpen]);

  function openPicker() {
    setVisibleYear(selected?.year ?? currentYear);
    setPickerOpen(true);
  }

  function closePicker() {
    setPickerOpen(false);
    window.requestAnimationFrame(() => triggerRef.current?.focus());
  }

  function chooseMonth(month: number) {
    onChange(`${visibleYear}-${String(month).padStart(2, '0')}`);
    closePicker();
  }

  function handlePickerKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if (event.key !== 'Escape') return;
    event.preventDefault();
    event.stopPropagation();
    closePicker();
  }

  return (
    <fieldset className={`expiry-field ${compact ? 'expiry-field-compact' : ''}`.trim()}>
      <legend>{compact ? t('expiry.label') : t('expiry.month')}</legend>
      <button
        ref={triggerRef}
        className={`expiry-trigger ${value ? 'has-value' : ''}`.trim()}
        type="button"
        aria-haspopup="dialog"
        aria-expanded={pickerOpen}
        onClick={openPicker}
      >
        <span>{formatExpiry(value)}</span>
        <span className="expiry-trigger-icon" aria-hidden="true">▾</span>
      </button>

      {pickerOpen && <div
        className="expiry-dialog-backdrop"
        role="presentation"
        onMouseDown={(event) => {
          event.preventDefault();
          event.stopPropagation();
          closePicker();
        }}
        onKeyDown={handlePickerKeyDown}
      >
        <section
          ref={dialogRef}
          className="expiry-dialog"
          role="dialog"
          aria-modal="true"
          aria-label={t('expiry.chooseMonthYear')}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="expiry-dialog-heading">
            <span>{t('expiry.chooseDate')}</span>
            <strong>{formatExpiry(value)}</strong>
          </div>
          <div className="expiry-year-selector">
            <button
              type="button"
              aria-label={t('expiry.previousYear')}
              disabled={visibleYear <= minYear}
              onClick={() => setVisibleYear((year) => Math.max(minYear, year - 1))}
            >‹</button>
            <strong aria-live="polite">{visibleYear}</strong>
            <button
              type="button"
              aria-label={t('expiry.nextYear')}
              disabled={visibleYear >= maxYear}
              onClick={() => setVisibleYear((year) => Math.min(maxYear, year + 1))}
            >›</button>
          </div>
          <div className="expiry-month-grid">
            {EXPIRY_MONTH_LABELS.map((label, index) => {
              const month = index + 1;
              const isSelected = selected?.year === visibleYear && selected.month === month;
              const isCurrent = currentYear === visibleYear && currentMonth === month;
              return <button
                key={month}
                className={`expiry-month ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''}`.trim()}
                type="button"
                aria-pressed={isSelected}
                aria-label={`${label} ${visibleYear}`}
                onClick={() => chooseMonth(month)}
              >{label}</button>;
            })}
          </div>
          <div className="expiry-dialog-actions">
            <button className="quiet-link" type="button" disabled={!value} onClick={() => { onChange(''); closePicker(); }}>{t('action.clear')}</button>
            <button className="secondary-button compact" type="button" onClick={closePicker}>{t('action.cancel')}</button>
          </div>
        </section>
      </div>}
    </fieldset>
  );
}


function exactArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const copy = new Uint8Array(bytes.byteLength);
  copy.set(bytes);
  return copy.buffer;
}

type AttachmentKind = 'note' | 'photoId';

function attachmentKindForPresentation(kind: PresentationKind): AttachmentKind | null {
  return kind === 'note' || kind === 'photoId' ? kind : null;
}

function normalizeAttachmentRefs(refs: AttachmentRef[], kind: PresentationKind): AttachmentRef[] {
  if (kind === 'photoId') {
    return refs
      .map((ref, index) => ({ ...ref, slot: ref.slot ?? (index === 0 ? 'front' : 'back') }))
      .filter((ref) => ref.slot === 'front' || ref.slot === 'back')
      .slice(0, 2);
  }
  if (kind === 'note') {
    const seen = new Set<string>();
    return refs
      .filter((ref) => {
        if (seen.has(ref.attachmentId)) return false;
        seen.add(ref.attachmentId);
        return true;
      })
      .map((ref) => ({ ...ref, slot: 'note' as const }));
  }
  return [];
}

function attachmentRefForSlot(
  refs: AttachmentRef[],
  kind: PresentationKind,
  slot: AttachmentSlot,
): AttachmentRef | undefined {
  return normalizeAttachmentRefs(refs, kind).find((ref) => ref.slot === slot);
}

function attachmentLabel(slot: AttachmentSlot, full = false): string {
  if (slot === 'back') return full ? 'Back of ID' : 'Back';
  return full ? 'Front of ID' : 'Front';
}

function formatAttachmentSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return 'Unknown size';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(bytes < 10 * 1024 * 1024 ? 1 : 0)} MB`;
}

async function loadLocalAttachmentPlaintext(
  ref: AttachmentRef,
  vaultKey: Uint8Array,
  recordId: string,
  preferOriginal = false,
): Promise<{ bytes: Uint8Array; mimeType: string } | null> {
  let stored = await loadLocalAttachment(ref.attachmentId);
  const preferredAvailable = preferOriginal
    ? Boolean(stored?.originalCiphertext)
    : Boolean(stored?.thumbnailCiphertext || stored?.originalCiphertext);
  if (!preferredAvailable) {
    stored = await ensureCloudAttachmentVariant(ref, recordId, preferOriginal);
  }
  if (!stored || stored.recordId !== recordId) return null;
  const hasThumbnail = Boolean(stored.thumbnailCiphertext && stored.thumbnailNonce && stored.thumbnailChecksum);
  const variant = preferOriginal || !hasThumbnail ? 'original' : 'thumbnail';
  const selectedCiphertext = variant === 'original' ? stored.originalCiphertext : stored.thumbnailCiphertext;
  if (!selectedCiphertext) return null;
  const ciphertext = new Uint8Array(selectedCiphertext);
  const bytes = await decryptAttachmentBytes(
    ciphertext,
    {
      cryptoVersion: stored.cryptoVersion,
      wrappedAttachmentKey: stored.wrappedAttachmentKey,
      keyNonce: stored.keyNonce,
      nonce: variant === 'original' ? stored.originalNonce : stored.thumbnailNonce!,
      checksumSha256: variant === 'original' ? stored.originalChecksum : stored.thumbnailChecksum!,
    },
    vaultKey,
    recordId,
    ref.attachmentId,
    variant,
  );
  return { bytes, mimeType: stored.mimeType || ref.mimeType || 'application/octet-stream' };
}

async function createLocalAttachmentUrl(
  ref: AttachmentRef,
  vaultKey: Uint8Array,
  recordId: string,
  preferOriginal = false,
): Promise<string | null> {
  const plaintext = await loadLocalAttachmentPlaintext(ref, vaultKey, recordId, preferOriginal);
  if (!plaintext) return null;
  try {
    return URL.createObjectURL(new Blob([exactArrayBuffer(plaintext.bytes)], { type: plaintext.mimeType }));
  } finally {
    await clearBytes(plaintext.bytes);
  }
}

function PhotoAttachmentViewer({ card, vaultKey }: { card: MemoryCard; vaultKey: Uint8Array }) {
  const { t } = useT();
  const refs = useMemo(
    () => normalizeAttachmentRefs(card.attachmentRefs, 'photoId'),
    [card.attachmentRefs],
  );
  const [previews, setPreviews] = useState<Partial<Record<AttachmentSlot, string>>>({});
  const [unavailable, setUnavailable] = useState<Set<AttachmentSlot>>(new Set());
  const [fullImage, setFullImage] = useState<{ url: string; label: string } | null>(null);
  const urlsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      for (const ref of refs) {
        const slot = ref.slot as AttachmentSlot;
        try {
          const url = await createLocalAttachmentUrl(ref, vaultKey, card.recordId);
          if (cancelled) {
            if (url) URL.revokeObjectURL(url);
            continue;
          }
          if (url) {
            urlsRef.current.add(url);
            setPreviews((current) => ({ ...current, [slot]: url }));
          } else {
            setUnavailable((current) => new Set(current).add(slot));
          }
        } catch {
          if (!cancelled) setUnavailable((current) => new Set(current).add(slot));
        }
      }
    })();
    return () => {
      cancelled = true;
      urlsRef.current.forEach((url) => URL.revokeObjectURL(url));
      urlsRef.current.clear();
    };
  }, [card.recordId, refs, vaultKey]);

  const closeFull = useCallback(() => {
    setFullImage((current) => {
      if (current) {
        URL.revokeObjectURL(current.url);
        urlsRef.current.delete(current.url);
      }
      return null;
    });
  }, []);

  useEffect(() => {
    if (!fullImage) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      requestOverlayClose();
    };
    window.addEventListener('keydown', onKeyDown, true);
    const unregister = registerOverlayBack(closeFull);
    return () => {
      window.removeEventListener('keydown', onKeyDown, true);
      unregister();
    };
  }, [fullImage, closeFull]);

  async function openFull(ref: AttachmentRef) {
    const label = attachmentLabel(ref.slot as AttachmentSlot, true);
    const url = await createLocalAttachmentUrl(ref, vaultKey, card.recordId, true);
    if (!url) return;
    urlsRef.current.add(url);
    setFullImage({ url, label });
  }

  if (refs.length === 0) return null;

  return <>
    <div className="photo-view-grid" aria-label={t('field.idPhotos')}>
      {refs.map((ref) => {
        const slot = ref.slot as AttachmentSlot;
        const label = attachmentLabel(slot);
        const preview = previews[slot];
        return <article className="photo-view-card" key={ref.attachmentId}>
          <span>{label}</span>
          {preview ? <button type="button" onClick={() => void openFull(ref)} aria-label={`Open ${label.toLowerCase()} full screen`}>
            <img src={preview} alt={`${label} for ${card.title}`} />
          </button> : <div className="photo-unavailable" role="note">
            {unavailable.has(slot) ? t('attach.photoNotOnDevice') : t('attach.openingPhoto')}
          </div>}
        </article>;
      })}
    </div>
    {fullImage && <div className="photo-lightbox" role="dialog" aria-modal="true" aria-label={fullImage.label} onMouseDown={(event) => event.stopPropagation()}>
      <button className="photo-lightbox-close" type="button" onClick={() => requestOverlayClose()}>{t('action.close')}</button>
      <img src={fullImage.url} alt={fullImage.label} />
    </div>}
  </>;
}

type OpenAttachment = {
  ref: AttachmentRef;
  kind: 'image' | 'pdf' | 'audio' | 'video' | 'text' | 'download';
  url?: string;
  text?: string;
  truncated?: boolean;
};

function NoteAttachmentList({
  refs,
  recordId,
  vaultKey,
  onRemove,
  disabled = false,
}: {
  refs: AttachmentRef[];
  recordId: string;
  vaultKey: Uint8Array;
  onRemove?: (ref: AttachmentRef) => void | Promise<void>;
  disabled?: boolean;
}) {
  const { t } = useT();
  const noteRefs = useMemo(() => normalizeAttachmentRefs(refs, 'note'), [refs]);
  const [previews, setPreviews] = useState<Record<string, string>>({});
  const [unavailable, setUnavailable] = useState<Set<string>>(new Set());
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [opened, setOpened] = useState<OpenAttachment | null>(null);
  const [viewerError, setViewerError] = useState('');
  const previewUrls = useRef<Set<string>>(new Set());

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      for (const ref of noteRefs) {
        if (!isInlineImageMime(ref.mimeType)) continue;
        try {
          const url = await createLocalAttachmentUrl(ref, vaultKey, recordId);
          if (cancelled) {
            if (url) URL.revokeObjectURL(url);
            continue;
          }
          if (url) {
            previewUrls.current.add(url);
            setPreviews((current) => ({ ...current, [ref.attachmentId]: url }));
          } else {
            setUnavailable((current) => new Set(current).add(ref.attachmentId));
          }
        } catch {
          if (!cancelled) setUnavailable((current) => new Set(current).add(ref.attachmentId));
        }
      }
    })();
    return () => {
      cancelled = true;
      previewUrls.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrls.current.clear();
    };
  }, [noteRefs, recordId, vaultKey]);

  function closeOpened() {
    if (opened?.url) URL.revokeObjectURL(opened.url);
    setOpened(null);
    setViewerError('');
  }

  useEffect(() => {
    if (!opened) return;
    return registerOverlayBack(closeOpened);
  }, [opened]);

  useEffect(() => {
    if (!opened) return;
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopImmediatePropagation();
      closeOpened();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [opened]);

  async function openAttachment(ref: AttachmentRef) {
    setOpeningId(ref.attachmentId);
    setViewerError('');
    let plaintext: { bytes: Uint8Array; mimeType: string } | null = null;
    try {
      plaintext = await loadLocalAttachmentPlaintext(ref, vaultKey, recordId, true);
      if (!plaintext) throw new Error(t('attach.notOnDevice'));
      const kind = attachmentPreviewKind(plaintext.mimeType);
      if (kind === 'text') {
        const previewLimit = 1024 * 1024;
        const slice = plaintext.bytes.subarray(0, Math.min(plaintext.bytes.byteLength, previewLimit));
        const url = URL.createObjectURL(new Blob([exactArrayBuffer(plaintext.bytes)], { type: plaintext.mimeType }));
        setOpened({
          ref,
          kind,
          url,
          text: new TextDecoder().decode(slice),
          truncated: plaintext.bytes.byteLength > previewLimit,
        });
      } else {
        const url = URL.createObjectURL(new Blob([exactArrayBuffer(plaintext.bytes)], { type: plaintext.mimeType }));
        setOpened({ ref, kind, url });
      }
    } catch (cause) {
      setViewerError(cause instanceof Error ? cause.message : t('attach.couldNotOpen'));
    } finally {
      await clearBytes(plaintext?.bytes);
      setOpeningId(null);
    }
  }

  function downloadOpened() {
    if (!opened?.url) return;
    const link = document.createElement('a');
    link.href = opened.url;
    link.download = opened.ref.name || 'attachment';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  function openOpenedInNewTab() {
    if (!opened?.url) return;
    const link = document.createElement('a');
    link.href = opened.url;
    link.target = '_blank';
    link.rel = 'noopener';
    document.body.appendChild(link);
    link.click();
    link.remove();
  }

  if (noteRefs.length === 0) return null;

  return <>
    <div className="note-attachment-list" aria-label={t('field.attachments')}>
      {noteRefs.map((ref) => {
        const preview = previews[ref.attachmentId];
        const kind = attachmentPreviewKind(ref.mimeType);
        return <article className="note-attachment-card" key={ref.attachmentId}>
          <button className="note-attachment-open" type="button" disabled={disabled || openingId !== null} onClick={() => void openAttachment(ref)}>
            <span className="note-attachment-thumb" aria-hidden="true">
              {preview ? <img src={preview} alt="" /> : <span>{kind === 'image' ? 'IMG' : kind === 'pdf' ? 'PDF' : kind === 'audio' ? 'AUDIO' : kind === 'video' ? 'VIDEO' : kind === 'text' ? 'TEXT' : 'FILE'}</span>}
            </span>
            <span className="note-attachment-info">
              <strong>{ref.name || 'Attachment'}</strong>
              <small>{openingId === ref.attachmentId ? t('attach.opening') : unavailable.has(ref.attachmentId) ? t('attach.notStored') : formatAttachmentSize(ref.sizeBytes)}</small>
            </span>
          </button>
          {onRemove && <button className="quiet-link note-attachment-delete" type="button" disabled={disabled || openingId !== null} onClick={() => void onRemove(ref)}>{t('action.delete')}</button>}
        </article>;
      })}
    </div>
    {viewerError && <p className="form-error" role="alert">{viewerError}</p>}
    {opened && <div className="attachment-viewer-backdrop" role="dialog" aria-modal="true" aria-label={opened.ref.name} onMouseDown={() => requestOverlayClose()}>
      <section className={`attachment-viewer attachment-viewer-${opened.kind}`} onMouseDown={(event) => event.stopPropagation()}>
        <div className="attachment-viewer-header">
          <button className="secondary-button compact" type="button" onClick={() => requestOverlayClose()}>{t('action.close')}</button>
          <div><strong>{opened.ref.name || t('attach.title')}</strong><small>{formatAttachmentSize(opened.ref.sizeBytes)}</small></div>
        </div>
        <div className="attachment-viewer-content">
          {opened.kind === 'image' && opened.url && <img src={opened.url} alt={opened.ref.name} />}
          {opened.kind === 'pdf' && opened.url && <iframe src={opened.url} title={opened.ref.name} />}
          {opened.kind === 'audio' && opened.url && <audio src={opened.url} controls />}
          {opened.kind === 'video' && opened.url && <video src={opened.url} controls />}
          {opened.kind === 'text' && <pre>{opened.text}</pre>}
          {opened.kind === 'download' && <div className="attachment-download-message"><p>{t('attach.previewUnavailable')}</p></div>}
        </div>
        {opened.truncated && <p className="attachment-preview-note">{t('attach.previewLimited')}</p>}
        {opened.url && <div className="attachment-viewer-actions">{opened.kind === 'pdf' && <button className="secondary-button" type="button" onClick={openOpenedInNewTab}>{t('attach.openPdf')}</button>}<button className="primary-button" type="button" onClick={downloadOpened}>{t('attach.download')}</button></div>}
      </section>
    </div>}
  </>;
}

function AttachmentViewer({ card, vaultKey }: { card: MemoryCard; vaultKey: Uint8Array }) {
  if (card.presentationKind === 'photoId') return <PhotoAttachmentViewer card={card} vaultKey={vaultKey} />;
  if (card.presentationKind === 'note') {
    return <NoteAttachmentList refs={card.attachmentRefs} recordId={card.recordId} vaultKey={vaultKey} />;
  }
  return null;
}

function ItemEditor({ mode, collections, vaultKey, onSave, onDelete, onClose, onOpenCollectionPicker }: {
  mode: NonNullable<EditorMode>;
  collections: Array<MaterializedRecord<CollectionRecord>>;
  vaultKey: Uint8Array;
  onSave: (card: MemoryCard, baseRevisionId: string | null, removedAttachmentIds: string[]) => Promise<void>;
  onDelete: (item: MaterializedRecord<MemoryCard>) => Promise<void>;
  onClose: () => void;
  onOpenCollectionPicker: (selected: string[], callback: (ids: string[]) => void) => void;
}) {
  const { t } = useT();
  const existing = mode.item?.payload;
  const editorRecordId = useRef(existing?.recordId ?? crypto.randomUUID()).current;
  const initialAttachmentRefs = useRef(normalizeAttachmentRefs(existing?.attachmentRefs ?? [], mode.kind)).current;
  const [attachmentRefs, setAttachmentRefs] = useState<AttachmentRef[]>(initialAttachmentRefs);
  const stagedAttachmentIds = useRef<Set<string>>(new Set());
  const removedAttachmentIds = useRef<Set<string>>(new Set());
  const preserveStagedOnUnmount = useRef(false);
  const previewUrls = useRef<Set<string>>(new Set());
  const [attachmentPreviews, setAttachmentPreviews] = useState<Partial<Record<AttachmentSlot, string>>>({});
  const [attachmentBusy, setAttachmentBusy] = useState<string | null>(null);
  const [attachmentError, setAttachmentError] = useState('');
  const [title, setTitle] = useState(existing?.title ?? mode.initialTitle ?? '');
  const [pinned, setPinned] = useState(existing?.pinned ?? false);
  const [fields, setFields] = useState<Record<string, string>>(() => ({
    body: existing ? fieldValue(existing, 'Body') : '',
    username: existing ? fieldValue(existing, 'Username') : '',
    password: existing ? fieldValue(existing, 'Password') : '',
    website: existing ? fieldValue(existing, 'Website') : '',
    notes: existing ? fieldValue(existing, 'Notes') : '',
    number: existing ? fieldValue(existing, mode.kind === 'photoId' ? 'Document number' : 'Card number') : '',
    expiry: existing ? fieldValue(existing, 'Expiry') : '',
    securityCode: existing ? fieldValue(existing, 'Security code') : '',
    pin: existing ? fieldValue(existing, 'PIN') : '',
    documentType: existing ? fieldValue(existing, 'Document type') : '',
  }));
  const [collectionIds, setCollectionIds] = useState(existing?.collectionIds ?? []);
  const [reminderDate, setReminderDate] = useState(existing?.reminder?.dueDate ?? '');
  const [reminderTime, setReminderTime] = useState(existing?.reminder?.localTime ?? '');
  const [showFields, setShowFields] = useState<Set<OptionalField>>(() => {
    const shown = new Set<OptionalField>();
    if (existing?.reminder) shown.add('reminder');
    if (existing?.collectionIds.length) shown.add('collections');
    if (fields.website) shown.add('website');
    if (fields.notes) shown.add('notes');
    if (mode.kind === 'photoId' && fields.expiry) shown.add('expiry');
    if (mode.kind === 'card' && fields.securityCode) shown.add('securityCode');
    if (fields.pin) shown.add('pin');
    return shown;
  });
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const editorRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const frame = requestAnimationFrame(() => editorRef.current?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, []);

  useEffect(() => () => {
    if (!preserveStagedOnUnmount.current) {
      void deleteLocalAttachments(stagedAttachmentIds.current).catch(() => undefined);
    }
  }, []);

  useEffect(() => {
    if (mode.kind !== 'photoId') return;
    let cancelled = false;
    void (async () => {
      for (const ref of initialAttachmentRefs) {
        const slot = ref.slot as AttachmentSlot;
        try {
          const url = await createLocalAttachmentUrl(ref, vaultKey, editorRecordId);
          if (cancelled) {
            if (url) URL.revokeObjectURL(url);
            continue;
          }
          if (url) {
            previewUrls.current.add(url);
            setAttachmentPreviews((current) => ({ ...current, [slot]: url }));
          }
        } catch {
          // A locally unavailable image remains represented by its encrypted attachment reference.
        }
      }
    })();
    return () => {
      cancelled = true;
      previewUrls.current.forEach((url) => URL.revokeObjectURL(url));
      previewUrls.current.clear();
    };
  }, [editorRecordId, initialAttachmentRefs, mode.kind, vaultKey]);

  function setField(key: string, value: string) {
    setFields((current) => ({ ...current, [key]: value }));
  }

  function reveal(field: OptionalField) {
    setShowFields((current) => new Set(current).add(field));
  }

  function replacePreview(slot: AttachmentSlot, url?: string) {
    setAttachmentPreviews((current) => {
      const existingUrl = current[slot];
      if (existingUrl) {
        URL.revokeObjectURL(existingUrl);
        previewUrls.current.delete(existingUrl);
      }
      const next = { ...current };
      if (url) {
        previewUrls.current.add(url);
        next[slot] = url;
      } else {
        delete next[slot];
      }
      return next;
    });
  }

  async function removePhotoAttachment(slot: AttachmentSlot) {
    const currentRef = attachmentRefForSlot(attachmentRefs, mode.kind, slot);
    if (!currentRef) return;
    if (stagedAttachmentIds.current.has(currentRef.attachmentId)) {
      await deleteLocalAttachment(currentRef.attachmentId);
      stagedAttachmentIds.current.delete(currentRef.attachmentId);
    } else {
      removedAttachmentIds.current.add(currentRef.attachmentId);
    }
    setAttachmentRefs((current) => normalizeAttachmentRefs(current, mode.kind).filter((ref) => ref.slot !== slot));
    replacePreview(slot);
    setAttachmentError('');
  }

  async function choosePhotoAttachment(slot: AttachmentSlot, file: File) {
    setAttachmentBusy(slot);
    setAttachmentError('');
    let originalBytes: Uint8Array | null = null;
    let thumbnailBytes: Uint8Array | null = null;
    try {
      const processed = await processPhotoFile(file);
      originalBytes = processed.originalBytes;
      thumbnailBytes = processed.thumbnailBytes;
      const attachmentId = crypto.randomUUID();
      const encrypted = await encryptAttachmentVariants(
        originalBytes,
        thumbnailBytes,
        vaultKey,
        editorRecordId,
        attachmentId,
      );
      const timestamp = nowIso();
      await saveLocalAttachment({
        attachmentId,
        recordId: editorRecordId,
        slot,
        cryptoVersion: encrypted.cryptoVersion,
        wrappedAttachmentKey: encrypted.wrappedAttachmentKey,
        keyNonce: encrypted.keyNonce,
        originalNonce: encrypted.original.nonce,
        originalChecksum: encrypted.original.checksumSha256,
        originalCiphertext: exactArrayBuffer(encrypted.original.ciphertext),
        thumbnailNonce: encrypted.thumbnail?.nonce,
        thumbnailChecksum: encrypted.thumbnail?.checksumSha256,
        thumbnailCiphertext: encrypted.thumbnail ? exactArrayBuffer(encrypted.thumbnail.ciphertext) : undefined,
        mimeType: processed.mimeType,
        sizeBytes: encrypted.original.ciphertext.byteLength,
        thumbnailSizeBytes: encrypted.thumbnail?.ciphertext.byteLength,
        state: 'local',
        createdAt: timestamp,
        updatedAt: timestamp,
      });

      const previous = attachmentRefForSlot(attachmentRefs, mode.kind, slot);
      if (previous) {
        if (stagedAttachmentIds.current.has(previous.attachmentId)) {
          await deleteLocalAttachment(previous.attachmentId);
          stagedAttachmentIds.current.delete(previous.attachmentId);
        } else {
          removedAttachmentIds.current.add(previous.attachmentId);
        }
      }
      stagedAttachmentIds.current.add(attachmentId);
      const nextRef: AttachmentRef = {
        attachmentId,
        slot,
        name: file.name || `${slot} photo`,
        mimeType: processed.mimeType,
        sizeBytes: encrypted.original.ciphertext.byteLength,
        state: 'local',
      };
      setAttachmentRefs((current) => [
        ...normalizeAttachmentRefs(current, mode.kind).filter((ref) => ref.slot !== slot),
        nextRef,
      ].sort((left, right) => left.slot === right.slot ? 0 : left.slot === 'front' ? -1 : 1));
      replacePreview(slot, URL.createObjectURL(new Blob([exactArrayBuffer(thumbnailBytes)], { type: processed.mimeType })));
    } catch (cause) {
      setAttachmentError(cause instanceof Error ? cause.message : t('editor.photoFailed'));
    } finally {
      await clearBytes(originalBytes);
      await clearBytes(thumbnailBytes);
      setAttachmentBusy(null);
    }
  }

  async function removeNoteAttachment(ref: AttachmentRef) {
    if (stagedAttachmentIds.current.has(ref.attachmentId)) {
      await deleteLocalAttachment(ref.attachmentId);
      stagedAttachmentIds.current.delete(ref.attachmentId);
    } else {
      removedAttachmentIds.current.add(ref.attachmentId);
    }
    setAttachmentRefs((current) => normalizeAttachmentRefs(current, 'note').filter((entry) => entry.attachmentId !== ref.attachmentId));
    setAttachmentError('');
  }

  async function chooseNoteAttachment(file: File) {
    setAttachmentBusy('note-new');
    setAttachmentError('');
    let originalBytes: Uint8Array | null = null;
    let thumbnailBytes: Uint8Array | null = null;
    try {
      const processed = await processNoteAttachmentFile(file);
      originalBytes = processed.originalBytes;
      thumbnailBytes = processed.thumbnailBytes ?? null;
      const attachmentId = crypto.randomUUID();
      const encrypted = await encryptAttachmentVariants(
        originalBytes,
        thumbnailBytes ?? undefined,
        vaultKey,
        editorRecordId,
        attachmentId,
      );
      const timestamp = nowIso();
      await saveLocalAttachment({
        attachmentId,
        recordId: editorRecordId,
        slot: 'note',
        cryptoVersion: encrypted.cryptoVersion,
        wrappedAttachmentKey: encrypted.wrappedAttachmentKey,
        keyNonce: encrypted.keyNonce,
        originalNonce: encrypted.original.nonce,
        originalChecksum: encrypted.original.checksumSha256,
        originalCiphertext: exactArrayBuffer(encrypted.original.ciphertext),
        thumbnailNonce: encrypted.thumbnail?.nonce,
        thumbnailChecksum: encrypted.thumbnail?.checksumSha256,
        thumbnailCiphertext: encrypted.thumbnail ? exactArrayBuffer(encrypted.thumbnail.ciphertext) : undefined,
        mimeType: processed.mimeType,
        sizeBytes: encrypted.original.ciphertext.byteLength,
        thumbnailSizeBytes: encrypted.thumbnail?.ciphertext.byteLength,
        state: 'local',
        createdAt: timestamp,
        updatedAt: timestamp,
      });
      stagedAttachmentIds.current.add(attachmentId);
      const nextRef: AttachmentRef = {
        attachmentId,
        slot: 'note',
        name: file.name || 'Attachment',
        mimeType: processed.mimeType,
        sizeBytes: encrypted.original.ciphertext.byteLength,
        state: 'local',
      };
      setAttachmentRefs((current) => [...normalizeAttachmentRefs(current, 'note'), nextRef]);
    } catch (cause) {
      setAttachmentError(cause instanceof Error ? cause.message : t('editor.attachmentFailed'));
    } finally {
      await clearBytes(originalBytes);
      await clearBytes(thumbnailBytes);
      setAttachmentBusy(null);
    }
  }

  async function cancelEditor() {
    await deleteLocalAttachments(stagedAttachmentIds.current).catch(() => undefined);
    onClose();
  }

  async function submit(event?: FormEvent) {
    event?.preventDefault();
    if (!title.trim()) return setError(t('editor.addTitle'));
    setBusy(true);
    setError('');
    try {
      const reminder: ReminderSpec | undefined = reminderDate ? {
        mode: 'cloud',
        dueDate: reminderDate,
        localTime: reminderTime || undefined,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        allDay: !reminderTime,
        recurrence: 'none',
      } : undefined;
      const card = cardFromDraft({
        id: editorRecordId,
        title,
        pinned,
        presentationKind: mode.kind,
        fields,
        collectionIds,
        reminder,
        attachmentRefs: attachmentKindForPresentation(mode.kind) ? normalizeAttachmentRefs(attachmentRefs, mode.kind) : existing?.attachmentRefs ?? [],
      }, existing);
      preserveStagedOnUnmount.current = true;
      try {
        await onSave(card, mode.item?.revisionId ?? null, [...removedAttachmentIds.current]);
      } catch (cause) {
        preserveStagedOnUnmount.current = false;
        throw cause;
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('editor.saveFailed'));
    } finally {
      setBusy(false);
    }
  }

  function handleKeyDown(event: ReactKeyboardEvent<HTMLElement>) {
    if ((event.ctrlKey || event.metaKey) && event.key === 'Enter') {
      event.preventDefault();
      void submit();
    }
  }

  const optionalActions: Array<{ field: OptionalField; labelKey: StringKey }> = [];
  if (mode.kind === 'password' && !showFields.has('website')) optionalActions.push({ field: 'website', labelKey: 'field.website' });
  if (mode.kind !== 'note' && !showFields.has('notes')) optionalActions.push({ field: 'notes', labelKey: 'field.notes' });
  if (mode.kind === 'photoId' && !showFields.has('expiry')) optionalActions.push({ field: 'expiry', labelKey: 'field.expiry' });
  if (mode.kind === 'card' && !showFields.has('securityCode')) optionalActions.push({ field: 'securityCode', labelKey: 'field.securityCode' });
  if (mode.kind === 'card' && !showFields.has('pin')) optionalActions.push({ field: 'pin', labelKey: 'field.pin' });
  if (!showFields.has('reminder')) optionalActions.push({ field: 'reminder', labelKey: 'field.reminder' });
  if (!showFields.has('collections')) optionalActions.push({ field: 'collections', labelKey: 'field.collections' });

  const collectionNames = collectionIds
    .map((id) => collections.find((entry) => entry.recordId === id)?.payload.name)
    .filter(Boolean)
    .join(', ');

  return (
    <div className="overlay editor-overlay" role="presentation">
      <section ref={editorRef} tabIndex={-1} className="sheet editor-sheet" role="dialog" aria-modal="true" aria-labelledby="editor-title" onKeyDown={handleKeyDown}>
        <div className="sheet-header editor-header"><button className="text-button" type="button" onClick={() => void cancelEditor()}>{t('action.cancel')}</button><h2 id="editor-title">{t(existing ? 'editor.editItem' : 'editor.newItem', { type: typeLabel(t, mode.kind) })}</h2><button className="text-button editor-top-save" disabled={busy} type="button" onClick={() => void submit()}>{busy ? t('action.saving') : t('action.save')}</button></div>
        <form className="editor-form" autoComplete="off" onSubmit={submit}>
          <label>{t('field.title')}<input autoFocus={!existing} value={title} onChange={(event) => setTitle(event.target.value)} placeholder={t('editor.titlePlaceholder', { type: typeLabel(t, mode.kind) })} /></label>
          {mode.kind === 'note' && <label>{t('field.body')}<textarea className="body-field" rows={6} value={fields.body} onChange={(event) => setField('body', event.target.value)} /></label>}
          {mode.kind === 'password' && <>
            <label>{t('field.username')}<input value={fields.username} onChange={(event) => setField('username', event.target.value)} autoComplete="off" /></label>
            <label>{t('field.password')}<SensitiveInput value={fields.password} onChange={(event) => setField('password', event.target.value)} /></label>
          </>}
          {mode.kind === 'card' && <>
            <label>{t('field.cardNumber')}<SensitiveInput inputMode="numeric" value={fields.number} onChange={(event) => setField('number', event.target.value)} /></label>
            <div className={`card-expiry-cvv-row ${showFields.has('securityCode') ? '' : 'without-cvv'}`.trim()}>
              <ExpiryField compact value={fields.expiry} onChange={(value) => setField('expiry', value)} />
              {showFields.has('securityCode') && <label className="cvv-field">{t('field.cvv')}<SensitiveInput inputMode="numeric" value={fields.securityCode} onChange={(event) => setField('securityCode', event.target.value)} /></label>}
            </div>
          </>}
          {mode.kind === 'photoId' && <>
            <div className="photo-editor-field">
              <span className="field-label">{t('field.idPhotos')}</span>
              <p>{t('editor.photoNote', { mb: MAX_PHOTO_INPUT_MB })}</p>
              <div className="photo-slot-grid">
                {(['front', 'back'] as const).map((slot) => {
                  const ref = attachmentRefForSlot(attachmentRefs, mode.kind, slot);
                  const preview = attachmentPreviews[slot];
                  const inputId = `photo-${editorRecordId}-${slot}`;
                  return <article className="photo-slot-card" key={slot}>
                    <div className="photo-slot-heading"><strong>{t(slot === 'front' ? 'editor.front' : 'editor.back')}</strong>{slot === 'back' && <small>{t('editor.optional')}</small>}</div>
                    {ref ? <div className="photo-slot-preview">
                      {preview ? <img src={preview} alt={`${slot} ID preview`} /> : <span>{t('editor.photoNotOnDevice')}</span>}
                    </div> : <label className="photo-slot-preview photo-slot-placeholder" htmlFor={inputId}>
                      <span>{t(slot === 'front' ? 'editor.tapAddFront' : 'editor.tapAddBack')}</span>
                    </label>}
                    <input
                      className="sr-only"
                      id={inputId}
                      type="file"
                      accept="image/*"
                      disabled={attachmentBusy !== null}
                      onChange={(event) => {
                        const input = event.currentTarget;
                        const file = input.files?.[0];
                        input.value = '';
                        if (file) void choosePhotoAttachment(slot, file);
                      }}
                    />
                    {/* A separate capture input opens the camera directly, since a plain file
                        input only offers whatever the OS picker chooses to show. */}
                    <input
                      className="sr-only"
                      id={`${inputId}-camera`}
                      type="file"
                      accept="image/*"
                      capture="environment"
                      disabled={attachmentBusy !== null}
                      onChange={(event) => {
                        const input = event.currentTarget;
                        const file = input.files?.[0];
                        input.value = '';
                        if (file) void choosePhotoAttachment(slot, file);
                      }}
                    />
                    <div className="photo-slot-actions">
                      <label className="secondary-button compact" htmlFor={`${inputId}-camera`}>{attachmentBusy === slot ? t('editor.preparing') : t('editor.takePhoto')}</label>
                      <label className="secondary-button compact" htmlFor={inputId}>{t('editor.choosePhoto')}</label>
                      {ref && <button className="quiet-link" type="button" disabled={attachmentBusy !== null} onClick={() => void removePhotoAttachment(slot)}>{t('action.remove')}</button>}
                    </div>
                  </article>;
                })}
              </div>
              {attachmentError && <p className="form-error" role="alert">{attachmentError}</p>}
            </div>
            <label>{t('field.documentType')}<select value={fields.documentType} onChange={(event) => setField('documentType', event.target.value)}><option value="" disabled>{t('editor.selectDocumentType')}</option>{PHOTO_ID_TYPES.map((type) => <option key={type} value={type}>{type}</option>)}</select></label>
            <label>{t('field.documentNumber')}<SensitiveInput value={fields.number} onChange={(event) => setField('number', event.target.value)} /></label>
          </>}

          {showFields.has('website') && <label>{t('field.website')}<input type="url" value={fields.website} onChange={(event) => setField('website', event.target.value)} /></label>}
          {showFields.has('notes') && <label>{t('field.notes')}<textarea rows={3} value={fields.notes} onChange={(event) => setField('notes', event.target.value)} /></label>}
          {showFields.has('expiry') && <ExpiryField value={fields.expiry} onChange={(value) => setField('expiry', value)} />}
          {showFields.has('pin') && <label>{t('field.pin')}<SensitiveInput inputMode="numeric" value={fields.pin} onChange={(event) => setField('pin', event.target.value)} /></label>}
          {showFields.has('reminder') && <div className="reminder-row">
            <label>{t('editor.reminderDate')}<input type="date" value={reminderDate} onChange={(event) => setReminderDate(event.target.value)} /></label>
            <label>{t('editor.optionalTime')}<input type="time" value={reminderTime} disabled={!reminderDate} onChange={(event) => setReminderTime(event.target.value)} /></label>
          </div>}
          {showFields.has('collections') && <div className="collection-field"><span><strong>{t('field.collections')}</strong><small>{collectionNames || t('editor.noneSelected')}</small></span><button className="secondary-button compact" type="button" onClick={() => onOpenCollectionPicker(collectionIds, setCollectionIds)}>{t('action.change')}</button></div>}
          {mode.kind === 'note' && attachmentRefs.length > 0 && <div className="note-attachment-field">
            <span className="field-label">{t('field.attachments')}</span>
            <p>{t('editor.attachmentNote')}</p>
            <NoteAttachmentList
              refs={attachmentRefs}
              recordId={editorRecordId}
              vaultKey={vaultKey}
              disabled={attachmentBusy !== null}
              onRemove={removeNoteAttachment}
            />
            {attachmentError && <p className="form-error" role="alert">{attachmentError}</p>}
          </div>}
          {(optionalActions.length > 0 || mode.kind === 'note') && <div className="optional-actions" aria-label={t('editor.optionalFields')}>
            {optionalActions.map(({ field, labelKey }) => <OptionalFieldButton key={field} label={t(labelKey)} onClick={() => reveal(field)} />)}
            {mode.kind === 'note' && <>
              <input
                className="sr-only"
                id={`note-attachment-${editorRecordId}`}
                type="file"
                multiple
                disabled={attachmentBusy !== null}
                onChange={(event) => {
                  const input = event.currentTarget;
                  const files = Array.from(input.files ?? []);
                  input.value = '';
                  if (files.length > 0) void (async () => {
                    for (const file of files) await chooseNoteAttachment(file);
                  })();
                }}
              />
              <label className={`optional-field-button ${attachmentBusy === 'note-new' ? 'disabled' : ''}`.trim()} htmlFor={`note-attachment-${editorRecordId}`}>
                + {attachmentBusy === 'note-new' ? t('editor.addingAttachment') : t('editor.attachmentButton', { mb: MAX_ATTACHMENT_INPUT_MB })}
              </label>
            </>}
          </div>}
          {error && <p className="form-error" role="alert">{error}</p>}
          <p className="field-hint">{t('editor.shortcutHint')}</p>
          <div className="editor-action-bar">
            <button
              className={`editor-pin-toggle ${pinned ? 'active' : ''}`}
              type="button"
              aria-pressed={pinned}
              onClick={() => setPinned((value) => !value)}
            >
              <span className="editor-pin-star" aria-hidden="true">{pinned ? '★' : '☆'}</span>
              <span>{pinned ? t('editor.pinned') : t('editor.pinItem')}</span>
            </button>
            <div className="editor-action-buttons">
              {mode.item && <details className="editor-more-menu">
                <summary className="secondary-button compact">{t('action.more')}</summary>
                <div className="editor-more-popover">
                  <button className="danger-menu-item" type="button" onClick={() => void onDelete(mode.item!)}>{t('editor.deleteItem')}</button>
                </div>
              </details>}
              <button className="primary-button editor-save" disabled={busy} type="button" onClick={() => void submit()}>{busy ? t('action.saving') : t('action.save')}</button>
            </div>
          </div>
        </form>
      </section>
    </div>
  );
}

function ItemViewer({ item, collections, vaultKey, onEdit, onClose, onToggleReminderCompleted }: {
  item: MaterializedRecord<MemoryCard>;
  collections: Array<MaterializedRecord<CollectionRecord>>;
  vaultKey: Uint8Array;
  onEdit: () => void;
  onClose: () => void;
  onToggleReminderCompleted?: (completed: boolean) => void;
}) {
  const card = item.payload;
  const { t } = useT();
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const body = fieldValue(card, 'Body');
  const collectionNames = card.collectionIds
    .map((id) => collections.find((entry) => entry.recordId === id)?.payload.name)
    .filter(Boolean) as string[];

  return (
    <div className="overlay editor-overlay" role="presentation" onMouseDown={onClose}>
      <section className="sheet item-viewer" role="dialog" aria-modal="true" aria-labelledby="item-view-title" onMouseDown={(event) => event.stopPropagation()}>
        <div className="sheet-header sticky">
          <button className="text-button" type="button" onClick={onClose}>{t('action.close')}</button>
          <h2>{typeLabel(t, card.presentationKind)}</h2>
          <button className="text-button" type="button" onClick={onEdit}>{t('action.edit')}</button>
        </div>
        <div className="item-view-content">
          <div className="item-view-heading">
            <span className="item-view-type"><TypeIcon type={card.presentationKind}/>{typeLabel(t, card.presentationKind)}</span>
            <h1 id="item-view-title">{card.title}</h1>
          </div>

          {card.reminder && <div className="item-reminder-banner" aria-label={t('field.reminder')}>
            <span>{t('field.reminder')}</span>
            <div className="item-reminder-end">
              <strong>{displayReminder(card)}</strong>
              {onToggleReminderCompleted && <label className="reminder-done-check">
                <input
                  type="checkbox"
                  checked={Boolean(card.reminder.completedAt)}
                  onChange={(event) => onToggleReminderCompleted(event.target.checked)}
                />
                {card.reminder.completedAt ? displayCompleted(t, card) : t('reminder.done')}
              </label>}
            </div>
          </div>}

          {card.presentationKind === 'photoId' && <AttachmentViewer card={card} vaultKey={vaultKey} />}

          {card.presentationKind === 'note' ? <>
            {body ? <div className="note-body">{body}</div> : <p className="note-empty">{t('viewer.noBody')}</p>}
            <AttachmentViewer card={card} vaultKey={vaultKey} />
          </> : (
            <dl className="item-details">
              {card.lines.map((line) => (
                <div className="item-detail" key={line.lineId}>
                  <dt>{fieldLabel(t, line.label)}</dt>
                  <dd className={line.semanticType === 'multiline' ? 'multiline-value' : ''}>
                    {line.concealed ? line.value ? <span className="concealed-value">
                      <span>{revealed.has(line.lineId) ? line.value : '••••••••••••'}</span>
                      <button type="button" onClick={() => setRevealed((current) => {
                        const next = new Set(current);
                        if (next.has(line.lineId)) next.delete(line.lineId); else next.add(line.lineId);
                        return next;
                      })} aria-label={`${revealed.has(line.lineId) ? t('action.hide') : t('action.show')} ${fieldLabel(t, line.label)}`} title={revealed.has(line.lineId) ? t('action.hide') : t('action.show')}>
                        <EyeIcon revealed={revealed.has(line.lineId)} />
                      </button>
                    </span> : <span className="empty-value">{t('viewer.notSet')}</span> : line.value}
                  </dd>
                </div>
              ))}
            </dl>
          )}

          {collectionNames.length > 0 && <dl className="item-metadata">
            <dt>{t('field.collections')}</dt><dd>{collectionNames.join(', ')}</dd>
          </dl>}
          <p className="field-hint">{t('viewer.escCloses')}</p>
        </div>
      </section>
    </div>
  );
}

function ItemRow({ item, onOpen, onTogglePinned, showType, labelMode }: {
  item: MaterializedRecord<MemoryCard>;
  onOpen: () => void;
  onTogglePinned: () => void;
  showType?: boolean;
  labelMode: AppSettings['categoryLabelMode'];
}) {
  const card = item.payload;
  const { t } = useT();
  const noteBody = card.presentationKind === 'note' ? fieldValue(card, 'Body') : '';
  const normalizedNote = noteBody.replace(/\s+/g, ' ').trim();
  const noteIsTruncated = card.presentationKind === 'note' && (noteBody.includes('\n') || normalizedNote.length > 110);
  const summary = card.presentationKind === 'note'
    ? `${normalizedNote.slice(0, noteIsTruncated ? 105 : undefined).trimEnd()}${noteIsTruncated ? '…' : ''}` || 'Note'
    : cardSummary(card);

  return (
    <article className={`item-row ${item.pending ? 'pending' : ''} ${item.conflicted ? 'conflicted' : ''}`}>
      <button className={`item-main ${showType ? `with-kind label-${labelMode}` : ''}`} type="button" onClick={onOpen}>
        {showType && <span className={`item-kind mode-${labelMode}`}>
          {labelMode !== 'name' && <TypeIcon type={card.presentationKind}/>} 
          {labelMode !== 'icon' && <span>{typeLabel(t, card.presentationKind)}</span>}
        </span>}
        {/* The reminder date leads the line: it is short and fixed-width, while the summary
            is long and ellipsised, so a trailing date was clipped on longer items. */}
        <span className="item-copy"><strong>{card.title}</strong><small>{card.reminder ? `${card.reminder.completedAt ? '✓ ' : ''}${displayReminder(card)} · ` : ''}{summary}{item.conflicted ? ' · Conflict' : ''}</small></span>
      </button>
      <button className={`pin-button ${card.pinned ? 'active' : ''}`} type="button" aria-label={card.pinned ? 'Unpin item' : 'Pin item'} onClick={onTogglePinned}>{card.pinned ? '★' : '☆'}</button>
    </article>
  );
}


function ConflictRow({ item, onOpen }: {
  item: MaterializedRecord<RecordPayload>;
  onOpen: () => void;
}) {
  const { t } = useT();
  const title = isMemoryCard(item.payload) ? item.payload.title : item.payload.name;
  const kind = isMemoryCard(item.payload) ? typeLabel(t, item.payload.presentationKind) : t('conflict.collection');
  const state = item.deleted ? t('conflict.deletedVersion') : t('conflict.multipleChanges');
  return (
    <article className="item-row conflicted">
      <button className="item-main with-kind label-name" type="button" onClick={onOpen}>
        <span className="item-kind mode-name"><span>{kind}</span></span>
        <span className="item-copy"><strong>{title || 'Untitled'}</strong><small>{state}</small></span>
      </button>
    </article>
  );
}

function ConflictResolver({ item, repository, vaultKey, onResolved, onClose }: {
  item: MaterializedRecord<RecordPayload>;
  repository: VaultRepository;
  vaultKey: Uint8Array;
  onResolved: () => Promise<void>;
  onClose: () => void;
}) {
  const { t } = useT();
  const [branches, setBranches] = useState<Array<MaterializedRecord<RecordPayload>>>([]);
  const [revealed, setRevealed] = useState<Set<string>>(new Set());
  const [busyRevision, setBusyRevision] = useState<string | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    void repository.loadConflictBranches(item.recordId, vaultKey)
      .then((versions) => { if (!cancelled) setBranches(versions); })
      .catch((cause) => { if (!cancelled) setError(cause instanceof Error ? cause.message : t('conflict.errOpen')); });
    return () => { cancelled = true; };
  }, [item.recordId, repository, vaultKey]);

  async function choose(branch: MaterializedRecord<RecordPayload>) {
    setBusyRevision(branch.revisionId);
    setError('');
    try {
      const payload: RecordPayload = isMemoryCard(branch.payload)
        ? { ...branch.payload, clientMetadata: { ...branch.payload.clientMetadata, updatedAt: nowIso() } }
        : { ...branch.payload, updatedAt: nowIso() };
      const sourceRevisionId = await repository.resolveConflict(
        item.recordId,
        payload,
        branch.recordKind,
        vaultKey,
        branch.deleted,
      );
      if (isMemoryCard(payload)) {
        const keptAttachmentIds = new Set(branch.deleted ? [] : payload.attachmentRefs.map((ref) => ref.attachmentId));
        const removedAttachmentIds = new Set(
          branches.flatMap((version) => isMemoryCard(version.payload)
            ? version.payload.attachmentRefs.map((ref) => ref.attachmentId)
            : []),
        );
        keptAttachmentIds.forEach((attachmentId) => removedAttachmentIds.delete(attachmentId));
        await cancelAttachmentDeletions(keptAttachmentIds);
        await queueAttachmentDeletions(removedAttachmentIds, item.recordId, sourceRevisionId);
      }
      await onResolved();
      void processAttachmentSyncQueue(true).catch((cause) => console.warn('Attachment conflict cleanup deferred.', cause));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('conflict.errResolve'));
    } finally {
      setBusyRevision(null);
    }
  }

  return (
    <div className="overlay editor-overlay" role="presentation">
      <section className="sheet conflict-sheet" role="dialog" aria-modal="true" aria-labelledby="conflict-title">
        <div className="sheet-header"><button className="text-button" type="button" onClick={onClose}>{t('action.cancel')}</button><div><p className="eyebrow">{t('conflict.title')}</p><h2 id="conflict-title">{t('conflict.chooseVersion')}</h2></div><span className="sheet-header-spacer" /></div>
        <p className="feature-note">{t('conflict.note')}</p>
        {branches.length === 0 && !error && <p className="small-empty">{t('conflict.loading')}</p>}
        <div className="conflict-versions">
          {branches.map((branch, branchIndex) => {
            const updatedAt = isMemoryCard(branch.payload) ? branch.payload.clientMetadata.updatedAt : branch.payload.updatedAt;
            const title = isMemoryCard(branch.payload) ? branch.payload.title : branch.payload.name;
            return (
              <article className={`conflict-version ${branch.deleted ? 'deleted-version' : ''}`} key={branch.revisionId}>
                <div className="conflict-version-heading"><span>Version {branchIndex + 1}</span><small>{new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(updatedAt))}</small></div>
                {branch.deleted && <p className="tombstone-label">{t('conflict.deleted')}</p>}
                <h3>{title || 'Untitled'}</h3>
                {isMemoryCard(branch.payload) ? <div className="conflict-lines">
                  {branch.payload.lines.map((line) => {
                    const revealKey = `${branch.revisionId}:${line.lineId}`;
                    const isRevealed = revealed.has(revealKey);
                    return <div className="conflict-line" key={line.lineId}><span>{line.label}</span><strong>{line.concealed && !isRevealed ? '••••••••' : line.value || '—'}</strong>{line.concealed && <button className="quiet-link" type="button" onClick={() => setRevealed((current) => { const next = new Set(current); if (next.has(revealKey)) next.delete(revealKey); else next.add(revealKey); return next; })}>{isRevealed ? 'Hide' : 'Reveal'}</button>}</div>;
                  })}
                </div> : <p className="field-hint">Collection name: {branch.payload.name}</p>}
                <button className="primary-button" disabled={Boolean(busyRevision)} type="button" onClick={() => void choose(branch)}>{busyRevision === branch.revisionId ? t('conflict.resolving') : branch.deleted ? t('conflict.keepDeleted') : t('conflict.useThis')}</button>
              </article>
            );
          })}
        </div>
        {error && <p className="form-error" role="alert">{error}</p>}
      </section>
    </div>
  );
}

function SectionHeading({ title, onMore }: { title: string; onMore?: () => void }) {
  const { t } = useT();
  return <div className="section-heading"><h2>{title}</h2>{onMore && <button className="quiet-link" type="button" onClick={onMore}>{t('action.showAll')}</button>}</div>;
}

/** One consistent, collapsible Settings section. Collapsed sections still show their state. */
function SettingsSection({ title, status, defaultOpen = false, children }: {
  title: string;
  status?: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}) {
  // `open` on <details> is a controlled attribute in React, so it must be tracked in
  // state and synced from onToggle. Passing a bare initial value desyncs on re-render.
  const [open, setOpen] = useState(defaultOpen);
  return (
    <details className="settings-section" open={open} onToggle={(event) => setOpen(event.currentTarget.open)}>
      <summary>
        <span className="settings-section-title">{title}</span>
        {status && <span className="settings-section-status">{status}</span>}
        <span className="settings-section-chevron" aria-hidden="true" />
      </summary>
      <div className="settings-section-body">{children}</div>
    </details>
  );
}

function FingerprintUnlockRow({ device, onDeviceUpdated }: {
  device: DeviceState;
  onDeviceUpdated: (device: DeviceState) => void;
}) {
  const { t } = useT();
  const [supported, setSupported] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const enabled = Boolean(device.helloCredentialId && device.wrappedDeviceSecret);

  useEffect(() => {
    let cancelled = false;
    void helloSupported().then((value) => { if (!cancelled) setSupported(value); });
    return () => { cancelled = true; };
  }, []);

  async function turnOn() {
    setBusy(true);
    setError('');
    let secret: Uint8Array | null = null;
    try {
      if (!confirm(fingerprintWarning(t, device))) {
        setBusy(false);
        return;
      }
      secret = await loadDeviceSecret(device);
      const enrollment = await enrollHello(device.walletLabel || device.deviceName);
      const wrappedDeviceSecret = await wrapDeviceSecretForHello(secret, enrollment.hardwareSecret);
      await clearBytes(enrollment.hardwareSecret);
      // enrollHello proved the read path works, so dropping the plaintext copy is safe.
      const updated: DeviceState = {
        ...device,
        deviceSecret: undefined,
        helloCredentialId: enrollment.credentialId,
        helloPrfSalt: enrollment.prfSalt,
        wrappedDeviceSecret,
        updatedAt: nowIso(),
      };
      await saveDeviceState(updated);
      onDeviceUpdated(updated);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('fingerprint.errOn'));
    } finally {
      await clearBytes(secret);
      setBusy(false);
    }
  }

  async function turnOff() {
    setBusy(true);
    setError('');
    let secret: Uint8Array | null = null;
    try {
      // Read it back through the fingerprint one last time, and only then put the
      // plaintext copy back — otherwise the wallet would have no way to open at all.
      secret = await loadDeviceSecret(device);
      const updated: DeviceState = { ...device, deviceSecret: encodeBytes(secret), updatedAt: nowIso() };
      delete updated.helloCredentialId;
      delete updated.helloPrfSalt;
      delete updated.wrappedDeviceSecret;
      await saveDeviceState(updated);
      onDeviceUpdated(updated);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('fingerprint.errOff'));
    } finally {
      await clearBytes(secret);
      setBusy(false);
    }
  }

  if (supported === null) return null;

  return (
    <>
      <div className="setting-action-row">
        <span className="setting-key">{t('fingerprint.title')}</span>
        {supported
          ? <span className="setting-inline-end">
              <span className="setting-value">{enabled ? 'On' : 'Off'}</span>
              {enabled
                ? <button className="secondary-button compact" disabled={busy} type="button" onClick={() => void turnOff()}>{busy ? 'Working…' : 'Turn off'}</button>
                : <button className="secondary-button compact" disabled={busy} type="button" onClick={() => void turnOn()}>{busy ? t('fingerprint.settingUp') : t('create.turnOn')}</button>}
            </span>
          : <span className="field-hint">{t('fingerprint.unavailable')}</span>}
      </div>
      {error && <p className="form-error" role="alert">{error}</p>}
    </>
  );
}

function AccountPanel({ device, database, vaultKey, onDeviceUpdated, onMergeIntoAccount }: {
  device: DeviceState;
  database: Awaited<ReturnType<typeof getDatabase>>;
  vaultKey: Uint8Array;
  onDeviceUpdated: (device: DeviceState) => void;
  onMergeIntoAccount: (email: string, password: string, pin: string) => Promise<void>;
}) {
  const { t, plural } = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [syncPin, setSyncPin] = useState('');
  const [mode, setMode] = useState<'signin' | 'signup'>('signin');
  const [sessionUserId, setSessionUserId] = useState<string | null>(null);
  const [accountEmail, setAccountEmail] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [requests, setRequests] = useState<Array<Record<string, string>>>([]);
  const [pairingFingerprints, setPairingFingerprints] = useState<Record<string, string>>({});
  const [invalidPairingRequests, setInvalidPairingRequests] = useState<Record<string, true>>({});
  const [pairingReview, setPairingReview] = useState<{ request: Record<string, string>; fingerprint: string } | null>(null);
  const [pairingConfirmed, setPairingConfirmed] = useState(false);
  const [approvingRequestId, setApprovingRequestId] = useState<string | null>(null);
  const [syncLabel, setSyncLabel] = useState('');
  const [queuedChanges, setQueuedChanges] = useState(0);

  const accountConnected = Boolean(sessionUserId && device.accountUserId && sessionUserId === device.accountUserId);
  const hasDifferentBrowserSession = Boolean(sessionUserId && (!device.accountUserId || sessionUserId !== device.accountUserId));

  const refresh = useCallback(async () => {
    const id = await currentUserId();
    setSessionUserId(id);
    let signedInEmail = '';
    if (supabase) {
      const { data } = await supabase.auth.getUser();
      signedInEmail = data.user?.email ?? '';
      setAccountEmail(signedInEmail || device.accountEmail || '');
    }

    const connected = Boolean(id && device.accountUserId && id === device.accountUserId);
    if (connected && signedInEmail && !device.accountEmail) {
      const updated: DeviceState = { ...device, accountEmail: signedInEmail, updatedAt: nowIso() };
      await saveDeviceState(updated);
      onDeviceUpdated(updated);
    }
    if (!connected || !supabase) {
      setRequests([]);
      setPairingFingerprints({});
      setInvalidPairingRequests({});
      setPairingReview(null);
      return;
    }

    const { data } = await supabase
      .from('device_pairing_requests')
      .select('id, requesting_device_name, requesting_public_key, expires_at, state')
      .eq('vault_id', device.vaultId)
      .eq('state', 'pending')
      .gt('expires_at', nowIso())
      .order('created_at', { ascending: false });
    const rows = (data ?? []) as Array<Record<string, string>>;
    const fingerprints: Record<string, string> = {};
    const invalid: Record<string, true> = {};
    await Promise.all(rows.map(async (request) => {
      try {
        fingerprints[request.id] = await deviceKeyFingerprint(request.requesting_public_key);
      } catch {
        invalid[request.id] = true;
      }
    }));
    setRequests(rows);
    setPairingFingerprints(fingerprints);
    setInvalidPairingRequests(invalid);
    setPairingReview((current) => current && rows.some((request) => request.id === current.request.id) ? current : null);
  }, [device.accountEmail, device.accountUserId, device.vaultId]);

  useEffect(() => { void refresh(); }, [refresh]);

  useEffect(() => {
    let cancelled = false;
    const update = async () => {
      const status = database.currentStatus;
      const queue = accountConnected && status.connected
        ? await database.getUploadQueueStats().catch(() => ({ count: 0 }))
        : { count: 0 };
      if (cancelled) return;
      setQueuedChanges(queue.count);
      if (!cloudSyncEnabled) return setSyncLabel(t('sync.notConfigured'));
      if (!accountConnected) return setSyncLabel(t('sync.off'));
      if (status.connecting) return setSyncLabel(t('sync.connecting'));
      if (status.connected && (status.dataFlowStatus.uploading || status.dataFlowStatus.downloading)) return setSyncLabel(t('sync.uploading'));
      if (status.connected && queue.count > 0) return setSyncLabel(t('sync.waiting'));
      if (status.connected) return setSyncLabel(t('sync.connected'));
      setSyncLabel(t('sync.offline'));
    };
    const dispose = database.registerListener({ statusChanged: () => { void update(); } });
    const timer = window.setInterval(() => void update(), 3000);
    void update();
    return () => { cancelled = true; dispose(); window.clearInterval(timer); };
  }, [accountConnected, database, t]);

  async function authenticate(event: FormEvent) {
    event.preventDefault();
    if (!supabase) return;
    setBusy(true); setError(''); setMessage('');
    try {
      if (mode === 'signup') {
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: { emailRedirectTo: window.location.origin },
        });
        if (signUpError) throw signUpError;
        if (!data.session) {
          setMessage(t('account.confirmEmail'));
          return;
        }
      } else {
        const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
        if (signInError) throw signInError;
      }

      if (!/^\d{6,12}$/.test(syncPin)) throw new Error(t('account.errPin'));
      const id = await currentUserId();
      if (!id) throw new Error(t('account.errSession'));
      const { data: userData } = await supabase.auth.getUser();
      const signedInEmail = userData.user?.email ?? email.trim();
      setAccountEmail(signedInEmail);
      const { data: existingVault, error: lookupError } = await supabase.rpc('my_vault_id');
      if (lookupError) throw lookupError;

      if (existingVault && String(existingVault) !== device.vaultId) {
        // The account already holds a different wallet. Add this wallet's items to it and
        // switch to it in one step — no second sign-in. The PIN entered is that wallet's PIN.
        await onMergeIntoAccount(signedInEmail || email.trim(), password, syncPin);
        return;
      }

      const localKey = await verifyLocalPin(device, syncPin);
      try {
        if (!existingVault) {
          const deviceAuthTokenHash = await hashDeviceAuthorizationToken(device.deviceAuthToken);
          const { error: bootstrapError } = await supabase.rpc('bootstrap_vault', {
            p_vault_id: device.vaultId,
            p_device_id: device.deviceId,
            p_device_name: device.deviceName,
            p_device_public_key: null,
            p_device_auth_token_hash: deviceAuthTokenHash,
          });
          if (bootstrapError) throw bootstrapError;
        }

        const keyPackage = await createAccountWalletKeyPackage(password, syncPin, localKey, device.vaultId);
        const { error: packageError } = await supabase.rpc('upsert_vault_key_package', {
          p_vault_id: device.vaultId,
          p_kdf: keyPackage.kdf,
          p_nonce: keyPackage.wrappedVaultRootKey.nonce,
          p_ciphertext: keyPackage.wrappedVaultRootKey.ciphertext,
        });
        if (packageError) throw packageError;

        const updated = { ...device, accountUserId: id, accountEmail: signedInEmail || device.accountEmail, updatedAt: nowIso() };
        await saveDeviceState(updated);
        onDeviceUpdated(updated);
        const connected = await connectCloudSync(database, updated);
        setSessionUserId(id);
        setSyncPin('');
        setMessage(connected ? t('account.connected') : t('account.connectedNoSync'));
        await refresh();
        return;
      } finally {
        await clearBytes(localKey);
      }
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('account.errConnect'));
    } finally {
      setBusy(false);
    }
  }

  async function resetAccountPassword() {
    setBusy(true);
    setError('');
    setMessage('');
    try {
      await sendAccountPasswordReset(email || accountEmail);
      setMessage(t('account.resetSent'));
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('account.errReset'));
    } finally {
      setBusy(false);
    }
  }

  async function reviewPairing(request: Record<string, string>) {
    setError('');
    setPairingConfirmed(false);
    try {
      const fingerprint = await deviceKeyFingerprint(request.requesting_public_key);
      setPairingFingerprints((current) => ({ ...current, [request.id]: fingerprint }));
      setPairingReview({ request, fingerprint });
    } catch {
      setInvalidPairingRequests((current) => ({ ...current, [request.id]: true }));
      setError(t('pairing.errInvalid'));
    }
  }

  async function approveReviewedPairing() {
    if (!supabase || !pairingReview || !pairingConfirmed) return;
    const { request, fingerprint } = pairingReview;
    setError('');
    setApprovingRequestId(request.id);
    try {
      const currentFingerprint = await deviceKeyFingerprint(request.requesting_public_key);
      if (currentFingerprint !== fingerprint) throw new Error(t('pairing.errChanged'));
      const wrapped = await sealVaultRootKeyForDevice(vaultKey, request.requesting_public_key);
      const { error: approvalError } = await supabase.rpc('approve_device_pairing', {
        p_request_id: request.id,
        p_wrapped_vrk: wrapped,
        p_approving_device_id: device.deviceId,
        p_device_auth_token: device.deviceAuthToken,
      });
      if (approvalError) throw approvalError;
      setMessage(t('pairing.approved', { device: request.requesting_device_name }));
      setPairingReview(null);
      setPairingConfirmed(false);
      await refresh();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : t('pairing.errApprove'));
    } finally {
      setApprovingRequestId(null);
    }
  }

  async function signOut() {
    if (!supabase) return;
    await disconnectCloudSync(database);
    await supabase.auth.signOut();
    const updated: DeviceState = { ...device, updatedAt: nowIso() };
    await saveDeviceState(updated);
    onDeviceUpdated(updated);
    setSessionUserId(null);
    setAccountEmail(device.accountEmail ?? '');
    setMessage(t('account.signedOut'));
  }

  if (!cloudConfigured) {
    return <p>{t('account.notConfigured')}</p>;
  }

  if (!accountConnected) {
    return (
      <div className="account-status-card">
        <div className="status-grid">
          <div className="setting-row"><span className="setting-key">{t('account.signedIn')}</span><span className="setting-value">{hasDifferentBrowserSession ? accountEmail || t('account.anotherAccount') : t('settings.notSignedIn')}</span></div>
          <div className="setting-row"><span className="setting-key">{t('account.wallet')}</span><span className="setting-value">{walletOwnerLabel(t, device)}</span></div>
          <div className="setting-row"><span className="setting-key">{t('settings.thisDevice')}</span><span className="setting-value">{walletDeviceStatus(t, device)}</span></div>
          <div className="setting-row"><span className="setting-key">{t('account.sync')}</span><span className="setting-value">{t('sync.off')}</span></div>
        </div>
        {hasDifferentBrowserSession && <p className="status-message">{t('account.differentSession')}</p>}
        <details className="signin-collapse">
        <summary>{t('account.signInSyncTitle')}</summary>
        <form className="compact-form" autoComplete="off" onSubmit={authenticate}>
          <label>{t('account.email')}<input type="email" autoComplete="email" autoCapitalize="none" autoCorrect="off" spellCheck={false} value={email} onChange={(event) => setEmail(event.target.value)} /></label>
          <label>{t('pin.accountPassword')}<SensitiveInput value={password} onChange={(event) => setPassword(event.target.value)} /></label>
          <label>{t('account.pinLabel')}<SensitiveInput inputMode="numeric" value={syncPin} onChange={(event) => setSyncPin(event.target.value.replace(/\D/g, '').slice(0, 12))} placeholder={t('account.pinPlaceholder')} /><small className="field-hint">{t('account.pinHint')}</small></label>
          <div className="segmented"><button className={mode === 'signin' ? 'active' : ''} type="button" onClick={() => setMode('signin')}>{t('account.signInTab')}</button><button className={mode === 'signup' ? 'active' : ''} type="button" onClick={() => setMode('signup')}>{t('account.createTab')}</button></div>
          {mode === 'signin' && <button className="quiet-link form-link" disabled={busy || !(email.trim() || accountEmail)} type="button" onClick={() => void resetAccountPassword()}>{t('account.forgotPassword')}</button>}
          <button className="primary-button" disabled={busy || !email || password.length < 8 || syncPin.length < 6} type="submit">{busy ? t('sync.connecting') : mode === 'signin' ? t('account.signInSync') : t('account.createSync')}</button>
        </form>
        {hasDifferentBrowserSession && <button className="secondary-button compact" type="button" onClick={() => void signOut()}>{t('account.signOutOf', { account: accountEmail || t('account.anotherAccount') })}</button>}
        </details>
        {message && <p className="status-message">{message}</p>}{error && <p className="form-error">{error}</p>}
      </div>
    );
  }

  return (
    <div className="account-status-card">
      <div className="status-grid">
        <div className="setting-row"><span className="setting-key">{t('account.signedInAs')}</span><span className="setting-value">{accountEmail || device.accountEmail || t('account.currentAccount')}</span></div>
        <div className="setting-row"><span className="setting-key">{t('account.wallet')}</span><span className="setting-value">{walletOwnerLabel(t, device)}</span></div>
        <div className="setting-row"><span className="setting-key">{t('settings.thisDevice')}</span><span className="setting-value">{walletDeviceStatus(t, device)}</span></div>
        <div className="setting-row"><span className="setting-key">{t('account.sync')}</span><span className="setting-value">{syncLabel}</span></div>
        <div className="setting-row"><span className="setting-key">{t('account.device')}</span><span className="setting-inline-end"><span className="setting-value">{device.deviceName}</span><button className="secondary-button compact" type="button" onClick={() => void signOut()}>{t('account.signOut')}</button></span></div>
      </div>
      {queuedChanges > 0 && <p className="sync-status-line"><span>{plural('queued', queuedChanges)}</span></p>}
      <details className="advanced-device-approval">
        <summary>{t('pairing.advanced')}</summary>
        <p className="field-hint">{t('pairing.advancedNote')}</p>
        {requests.length === 0 && <p className="small-empty">{t('pairing.none')}</p>}
        {requests.map((request) => {
          const invalid = Boolean(invalidPairingRequests[request.id]);
          return (
            <div className="approval-card" key={request.id}>
              <span>
                <strong>{request.requesting_device_name}</strong>
                <small className={invalid ? 'pairing-key-invalid' : 'pairing-fingerprint-inline'}>
                  {invalid ? t('pairing.invalidKey') : pairingFingerprints[request.id] ?? t('pairing.calculating')}
                </small>
              </span>
              <button className="primary-button compact" disabled={invalid || !pairingFingerprints[request.id]} type="button" onClick={() => void reviewPairing(request)}>{t('pairing.review')}</button>
            </div>
          );
        })}
      </details>
      {message && <p className="status-message">{message}</p>}{error && <p className="form-error">{error}</p>}
      {pairingReview && (
        <div className="overlay pairing-review-overlay" role="presentation" onMouseDown={() => { if (!approvingRequestId) { setPairingReview(null); setPairingConfirmed(false); } }}>
          <section className="sheet pairing-review-sheet" role="dialog" aria-modal="true" aria-labelledby="pairing-review-title" onMouseDown={(event) => event.stopPropagation()}>
            <div className="sheet-header">
              <button className="text-button" disabled={Boolean(approvingRequestId)} type="button" onClick={() => { setPairingReview(null); setPairingConfirmed(false); }}>{t('action.cancel')}</button>
              <h2 id="pairing-review-title">{t('pairing.reviewTitle')}</h2>
              <span className="sheet-header-spacer" />
            </div>
            <p className="pairing-review-device">{pairingReview.request.requesting_device_name}</p>
            <p className="eyebrow">{t('pairing.fingerprint')}</p>
            <p className="pairing-fingerprint pairing-fingerprint-dialog">{pairingReview.fingerprint}</p>
            <p>{t('pairing.compare')}</p>
            <p className="pairing-warning"><strong>{t('pairing.doNotApprove')}</strong> {t('pairing.mismatchNote')}</p>
            <label className="pairing-confirmation-check">
              <input checked={pairingConfirmed} type="checkbox" onChange={(event) => setPairingConfirmed(event.target.checked)} />
              <span>{t('pairing.confirm')}</span>
            </label>
            <button className="primary-button" disabled={!pairingConfirmed || Boolean(approvingRequestId)} type="button" onClick={() => void approveReviewedPairing()}>
              {approvingRequestId ? t('pairing.approving') : t('pairing.approve')}
            </button>
          </section>
        </div>
      )}
    </div>
  );
}

/**
 * The language has to wrap every screen, including the ones App returns early, so the
 * provider sits above the app body. The body reports the resolved language back up as soon
 * as saved settings load; until then the browser's own language is used.
 */
export default function App() {
  const [locale, setLocale] = useState<Locale>(() => detectLocale());
  // Saved here rather than in the app body so the picker also works on the screens shown
  // before a wallet is open, where the body's settings state does not exist yet.
  const changeLanguage = useCallback((next: Locale) => {
    setLocale(next);
    void (async () => {
      const current = await loadSettings();
      await saveSettings({ ...current, language: next });
    })().catch(() => undefined);
  }, []);
  return <LocaleProvider locale={locale} onChange={changeLanguage}><AppBody onLocaleResolved={setLocale} /></LocaleProvider>;
}

function AppBody({ onLocaleResolved }: { onLocaleResolved: (locale: Locale) => void }) {
  const { t, plural, locale } = useT();
  const { setLocale } = useLocale();
  const [loading, setLoading] = useState(true);
  const [passwordRecovery, setPasswordRecovery] = useState(isPasswordRecoveryPending());
  const [walletRecovery, setWalletRecovery] = useState(false);
  const [switchingWallet, setSwitchingWallet] = useState(false);
  // When set, the setup gate opens straight to "Open my existing wallet" with this email
  // pre-filled — used when turning on sync finds the account already has a wallet.
  const [openExistingEmail, setOpenExistingEmail] = useState<string | null>(null);
  const [walletChosen, setWalletChosen] = useState(false);
  const [device, setDevice] = useState<DeviceState | null>(null);
  const [savedDevices, setSavedDevices] = useState<DeviceState[]>([]);
  const [settings, setSettings] = useState<AppSettings | null>(null);
  const [unlocked, setUnlocked] = useState(false);
  const [database, setDatabase] = useState<Awaited<ReturnType<typeof getDatabase>> | null>(null);
  const [repository, setRepository] = useState<VaultRepository | null>(null);
  const [snapshot, setSnapshot] = useState<VaultSnapshot>(EMPTY_SNAPSHOT);
  const [screen, setScreen] = useState<Screen>('home');
  const [activeCollectionId, setActiveCollectionId] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [menuOpen, setMenuOpen] = useState(false);
  const [chooserOpen, setChooserOpen] = useState(false);
  const [editor, setEditor] = useState<EditorMode>(null);
  const [viewer, setViewer] = useState<MaterializedRecord<MemoryCard> | null>(null);
  const [conflictItem, setConflictItem] = useState<MaterializedRecord<RecordPayload> | null>(null);
  const [collectionPicker, setCollectionPicker] = useState<{ selected: string[]; callback: (ids: string[]) => void } | null>(null);
  const [changePinOpen, setChangePinOpen] = useState(false);
  const [pinMessage, setPinMessage] = useState('');
  const [newCollectionName, setNewCollectionName] = useState('');
  const [saving, setSaving] = useState(false);
  const [syncStatusText, setSyncStatusText] = useState('');
  const [error, setError] = useState('');
  const [mergeNotice, setMergeNotice] = useState<string | null>(null);
  // The local wallet that items were just copied out of. It stays on the browser until the
  // account wallet has finished backing the copied items up, then can be safely removed.
  const [mergedFrom, setMergedFrom] = useState<{ vaultId: string; label: string } | null>(null);
  const [mergedBackupReady, setMergedBackupReady] = useState(false);
  const vaultKeyRef = useRef<Uint8Array | null>(null);
  const idleTimer = useRef<number | null>(null);
  const refreshInFlight = useRef(false);
  // Decrypted items stashed from the local wallet, to be copied into the account wallet
  // once it opens. Guarded by the target email so it only ever lands in the right wallet.
  const pendingMergeRef = useRef<{ targetEmail: string; merge: PreparedWalletMerge; sourceVaultId: string; sourceLabel: string } | null>(null);

  // Only let the old wallet be removed once the account wallet is connected and has drained
  // its upload queue — i.e. the copied items are safely backed up.
  useEffect(() => {
    if (!mergedFrom || !database) return;
    let cancelled = false;
    const check = async () => {
      const status = database.currentStatus;
      if (!status.connected) return;
      const queue = await database.getUploadQueueStats().catch(() => ({ count: 1 }));
      if (!cancelled && queue.count === 0) setMergedBackupReady(true);
    };
    const dispose = database.registerListener({ statusChanged: () => { void check(); } });
    const timer = window.setInterval(() => void check(), 3000);
    void check();
    return () => { cancelled = true; dispose(); window.clearInterval(timer); };
  }, [mergedFrom, database]);

  useEffect(() => {
    document.documentElement.dataset.rowSpacing = settings?.rowSpacing ?? 'large';
  }, [settings?.rowSpacing]);

  useEffect(() => {
    document.documentElement.dataset.fontScale = settings?.fontScale ?? 'medium';
  }, [settings?.fontScale]);

  // No saved choice yet means follow the browser, so a first-time user is not stranded on an
  // English landing screen before the language picker is reachable.
  useEffect(() => {
    const resolved = isLocale(settings?.language) ? settings.language : detectLocale();
    onLocaleResolved(resolved);
  }, [settings?.language, onLocaleResolved]);

  useEffect(() => {
    document.documentElement.lang = locale;
    // The picker saves the language itself, so mirror it here too; otherwise saving any
    // other setting would write the previous language back over the new one.
    setSettings((current) => current && current.language !== locale ? { ...current, language: locale } : current);
  }, [locale]);

  useEffect(() => {
    return subscribeToPasswordRecovery(() => setPasswordRecovery(true));
  }, []);

  useEffect(() => {
    void (async () => {
      try {
        const [state, profiles, appSettings, db] = await Promise.all([loadDeviceState(), loadDeviceStates(), loadSettings(), getDatabase()]);
        // The search projection is plaintext by design and is rebuilt only after unlock.
        // Purge it on every startup so an interrupted prior session cannot leave searchable content behind.
        await db.execute('DELETE FROM search_projection');
        setSavedDevices(profiles);
        setWalletChosen(profiles.length <= 1);
        setDevice(state);
        setSettings(appSettings);
        setDatabase(db);
      } catch (cause) {
        setError(cause instanceof Error ? cause.message : t('status.errInit'));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const refresh = useCallback(async () => {
    if (!repository || !vaultKeyRef.current || refreshInFlight.current) return;
    refreshInFlight.current = true;
    try {
      setSnapshot(await repository.loadSnapshot(vaultKeyRef.current));
      setSaving(false);
    } finally {
      refreshInFlight.current = false;
    }
  }, [repository]);

  // Plain-English sync status for the header, kept live from the sync connection.
  useEffect(() => {
    if (!database) return;
    const update = () => {
      if (!device?.accountUserId || !cloudSyncEnabled) return setSyncStatusText(t('status.onDeviceOnly'));
      const status = database.currentStatus;
      if (status.connecting) return setSyncStatusText(t('sync.connecting'));
      if (status.connected && (status.dataFlowStatus?.uploading || status.dataFlowStatus?.downloading)) return setSyncStatusText(t('status.syncing'));
      if (status.connected) return setSyncStatusText(t('status.synced'));
      return setSyncStatusText(t('status.offline'));
    };
    const dispose = database.registerListener({ statusChanged: () => update() });
    const timer = window.setInterval(update, 3000);
    update();
    return () => { dispose(); window.clearInterval(timer); };
  }, [database, device, t]);

  useEffect(() => {
    if (!repository || !unlocked) return;
    const unwatch = repository.watchChanges(() => void refresh());
    return unwatch;
  }, [repository, refresh, unlocked]);

  const lock = useCallback(async () => {
    if (idleTimer.current) window.clearTimeout(idleTimer.current);
    idleTimer.current = null;
    setEditor(null); setViewer(null); setConflictItem(null); setChooserOpen(false); setCollectionPicker(null); setChangePinOpen(false); setMenuOpen(false);
    await repository?.clearSearchProjection().catch((cause) => console.warn('Could not clear the local search projection.', cause));
    await clearBytes(vaultKeyRef.current);
    vaultKeyRef.current = null;
    clearAttachmentSyncContext();
    setUnlocked(false);
    setSnapshot(EMPTY_SNAPSHOT);
    setScreen('home');
  }, [repository]);

  const resetIdleTimer = useCallback(() => {
    if (!unlocked || !settings) return;
    if (idleTimer.current) window.clearTimeout(idleTimer.current);
    idleTimer.current = window.setTimeout(() => void lock(), settings.autoLockMinutes * 60_000);
  }, [lock, settings, unlocked]);

  useEffect(() => {
    if (!unlocked) return;
    const events: Array<keyof WindowEventMap> = ['pointerdown', 'keydown', 'touchstart'];
    events.forEach((name) => window.addEventListener(name, resetIdleTimer, { passive: true }));
    resetIdleTimer();
    return () => events.forEach((name) => window.removeEventListener(name, resetIdleTimer));
  }, [resetIdleTimer, unlocked]);

  useEffect(() => {
    if (!unlocked) return;
    if (!window.history.state?.safeWallet) {
      window.history.replaceState({ safeWallet: true, screen: 'home', layer: 'screen' } satisfies HistoryState, '');
    }
    const onPop = () => {
      if (runOverlayBack()) return;
      if (changePinOpen) return setChangePinOpen(false);
      if (collectionPicker) return setCollectionPicker(null);
      if (conflictItem) return setConflictItem(null);
      if (editor) return setEditor(null);
      if (viewer) return setViewer(null);
      if (chooserOpen) return setChooserOpen(false);
      if (screen !== 'home' || activeCollectionId) { setScreen('home'); setActiveCollectionId(null); return; }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [activeCollectionId, changePinOpen, chooserOpen, collectionPicker, conflictItem, editor, screen, unlocked, viewer]);

  useEffect(() => {
    if (!unlocked) return;
    const onEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (changePinOpen || collectionPicker || conflictItem || editor || viewer || chooserOpen) {
        event.preventDefault();
        closeLayer();
        return;
      }
      if (menuOpen) setMenuOpen(false);
    };
    window.addEventListener('keydown', onEscape);
    return () => window.removeEventListener('keydown', onEscape);
  }, [changePinOpen, chooserOpen, collectionPicker, conflictItem, editor, menuOpen, unlocked, viewer]);

  useEffect(() => {
    if (!unlocked || !database || !device?.accountUserId || !r2SyncEnabled) return;
    configureAttachmentSync(database, device);
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      void processAttachmentSyncQueue().catch((cause) => console.warn('Attachment sync deferred.', cause));
    };
    run();
    const timer = window.setInterval(run, 15_000);
    window.addEventListener('online', run);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
      window.removeEventListener('online', run);
    };
  }, [database, device, unlocked]);

  async function enterOpenState(state: DeviceState, key: Uint8Array) {
    if (!database) throw new Error(t('status.errDatabase'));
    setOpenExistingEmail(null);
    const repo = new VaultRepository(database, state.vaultId, state.deviceId);
    const next = await repo.loadSnapshot(key);
    const merged = await applyPendingMerge(repo, key, state);
    const openedSnapshot = merged ? await repo.loadSnapshot(key) : next;
    const previousKey = vaultKeyRef.current;
    vaultKeyRef.current = key;
    if (previousKey && previousKey !== key) await clearBytes(previousKey);
    setDevice(state);
    setSavedDevices(await loadDeviceStates());
    setRepository(repo);
    configureAttachmentSync(database, state);
    setUnlocked(true);
    setScreen('home');
    setSnapshot(openedSnapshot);
    if (state.accountUserId) await connectCloudSync(database, state).catch((cause) => console.warn('Sync connection deferred.', cause));
  }

  // Copy the prepared local items into the wallet that just opened, but only if it is the
  // account wallet we intended (matched by email). Attachments were decrypted from the
  // source wallet and re-encrypted for this wallet before switching; the source stays intact.
  async function applyPendingMerge(repo: VaultRepository, key: Uint8Array, state: DeviceState): Promise<boolean> {
    const pending = pendingMergeRef.current;
    if (!pending) return false;
    if (!state.accountEmail || state.accountEmail.trim().toLowerCase() !== pending.targetEmail.trim().toLowerCase()) {
      pendingMergeRef.current = null;
      throw new Error(t('merge.errMismatch'));
    }

    for (const collection of pending.merge.collections) {
      await repo.savePayload(collection, 'collection', key, null);
    }
    const attachmentsByRecord = new Map<string, typeof pending.merge.attachments>();
    for (const attachment of pending.merge.attachments) {
      const current = attachmentsByRecord.get(attachment.recordId) ?? [];
      current.push(attachment);
      attachmentsByRecord.set(attachment.recordId, current);
    }
    for (const card of pending.merge.cards) {
      const cardAttachments = attachmentsByRecord.get(card.recordId) ?? [];
      try {
        for (const attachment of cardAttachments) await saveLocalAttachment(attachment);
        await repo.savePayload(card, 'card', key, null);
      } catch (cause) {
        await deleteLocalAttachments(cardAttachments.map((attachment) => attachment.attachmentId)).catch(() => undefined);
        throw cause;
      }
    }

    setMergeNotice(
      t('merge.notice', { items: plural('items', pending.merge.cards.length), name: pending.sourceLabel }),
    );
    setMergedFrom({ vaultId: pending.sourceVaultId, label: pending.sourceLabel });
    setMergedBackupReady(false);
    pendingMergeRef.current = null;
    return true;
  }

  // After a merge, the old local wallet may be removed once the account wallet has confirmed
  // the copied items are backed up (connected and nothing left in the upload queue).
  async function removeMergedSource() {
    if (!mergedFrom || !mergedBackupReady) return;
    if (!confirm(t('merge.confirmRemoveOld', { name: mergedFrom.label }))) return;
    await deleteDeviceState(mergedFrom.vaultId);
    setSavedDevices(await loadDeviceStates());
    setMergedFrom(null);
    setMergedBackupReady(false);
    setMergeNotice(null);
  }

  // One-step merge from the Account panel: the account already has a different wallet, so
  // open it with the entered account password + that wallet's PIN, copy this wallet's items
  // in, and switch to it. No second sign-in.
  async function mergeIntoAccount(accountEmail: string, password: string, accountPin: string) {
    if (!supabase || !database) throw new Error('Sync is not configured for this deployment.');
    const stashCards = snapshot.cards.map((entry) => entry.payload);
    const stashCollections = snapshot.collections.map((entry) => entry.payload);
    const sourceVaultKey = vaultKeyRef.current;
    if (!sourceVaultKey || !device) throw new Error(t('merge.errOpenLocal'));
    const sourceVaultId = device.vaultId;
    const sourceLabel = walletProfileTitle(t, device);

    const { data: vaultId, error: vaultError } = await supabase.rpc('my_vault_id');
    if (vaultError) throw vaultError;
    if (!vaultId) throw new Error(t('merge.errNoWallet'));
    const { data: packageRow, error: packageLookupError } = await supabase.rpc('my_vault_key_package');
    if (packageLookupError) throw packageLookupError;
    const packageRecord = Array.isArray(packageRow) ? packageRow[0] : packageRow;
    if (!packageRecord) throw new Error(t('merge.errKeyPackage'));

    let accountKey: Uint8Array;
    try {
      accountKey = await openAccountWalletKeyPackage(password, accountPin, accountWalletKeyPackageFromRow(packageRecord as Record<string, unknown>), String(vaultId));
    } catch {
      throw new Error(t('merge.errPin'));
    }

    const count = stashCards.length;
    if (!confirm(t('merge.confirmAdd', { items: plural('items', count) }))) {
      await clearBytes(accountKey);
      return;
    }

    // Prepare every attachment before registering or switching devices. If even one file
    // is unavailable or cannot be decrypted, the merge stops and the source wallet remains
    // untouched rather than creating an incomplete copy.
    let preparedMerge: PreparedWalletMerge;
    try {
      preparedMerge = await prepareWalletMerge(
        stashCards,
        stashCollections,
        sourceVaultKey,
        accountKey,
      );
    } catch (cause) {
      await clearBytes(accountKey);
      throw cause;
    }

    let secret: Uint8Array | null = null;
    try {
      secret = await createDeviceSecret();
      const deviceAuthToken = await createDeviceAuthorizationToken();
      const deviceAuthTokenHash = await hashDeviceAuthorizationToken(deviceAuthToken);
      const deviceId = crypto.randomUUID();
      const name = defaultDeviceName();
      const envelope = await createPinEnvelope(accountPin, secret, accountKey);
      const { error: registerError } = await supabase.rpc('register_wallet_device_from_key_package', {
        p_device_id: deviceId,
        p_device_name: name,
        p_device_auth_token_hash: deviceAuthTokenHash,
      });
      if (registerError) throw registerError;
      const userId = await currentUserId();
      const timestamp = nowIso();
      const accountState: DeviceState = {
        version: 1,
        deviceId,
        deviceName: name,
        vaultId: String(vaultId),
        deviceSecret: encodeBytes(secret),
        deviceAuthToken,
        pinEnvelope: envelope,
        accountUserId: userId ?? undefined,
        accountEmail: accountEmail || undefined,
        createdAt: timestamp,
        updatedAt: timestamp,
      };
      await saveDeviceState(accountState);
      pendingMergeRef.current = { targetEmail: accountEmail, merge: preparedMerge, sourceVaultId, sourceLabel };
      try {
        await enterOpenState(accountState, accountKey);
      } catch (cause) {
        pendingMergeRef.current = null;
        await clearBytes(accountKey);
        throw cause;
      }
    } finally {
      await clearBytes(secret);
    }
  }

  // When fingerprint unlock is on, reading the secret prompts for the fingerprint, so
  // one Unlock button covers both factors: the typed PIN and the device check.
  async function unlock(pin: string) {
    if (!device) return;
    await unlockWith(pin, () => loadDeviceSecret(device));
  }

  async function unlockWith(pin: string, readDeviceSecret: () => Promise<Uint8Array>) {
    if (!device) return;
    // Read outside the try below so a cancelled fingerprint is not counted as a wrong PIN.
    const secret = await readDeviceSecret();
    try {
      const key = await unlockVaultRootKey(pin, secret, device.pinEnvelope);
      // Lazily upgrade an older, weaker PIN envelope to the current key-stretching cost.
      const upgradedEnvelope = pinEnvelopeNeedsUpgrade(device.pinEnvelope)
        ? await createPinEnvelope(pin, secret, key)
        : null;
      const cleared: DeviceState = (upgradedEnvelope || device.failedUnlocks || device.retryAfter)
        ? {
            ...device,
            pinEnvelope: upgradedEnvelope ?? device.pinEnvelope,
            failedUnlocks: 0,
            retryAfter: undefined,
            updatedAt: nowIso(),
          }
        : device;
      if (cleared !== device) await saveDeviceState(cleared);
      await enterOpenState(cleared, key);
    } catch (cause) {
      // Wrong PIN (or damaged device data): record the failed attempt so the
      // unlock screen can slow repeats and escalate to recovery.
      const attempts = (device.failedUnlocks ?? 0) + 1;
      const delayMs = pinRetryDelayMs(attempts);
      const updated: DeviceState = {
        ...device,
        failedUnlocks: attempts,
        retryAfter: delayMs > 0 ? new Date(Date.now() + delayMs).toISOString() : undefined,
        updatedAt: nowIso(),
      };
      await saveDeviceState(updated);
      setDevice(updated);
      throw cause;
    } finally {
      await clearBytes(secret);
    }
  }

  function navigate(next: Screen, collectionId: string | null = null) {
    setMenuOpen(false); setScreen(next); setActiveCollectionId(collectionId);
    if (next === 'home') setQuery('');
    window.history.pushState({ safeWallet: true, screen: next, layer: 'screen' } satisfies HistoryState, '');
  }

  function openChooser() {
    setChooserOpen(true);
    window.history.pushState({ safeWallet: true, screen, layer: 'chooser' } satisfies HistoryState, '');
  }

  function openChangePin() {
    setPinMessage('');
    setChangePinOpen(true);
    window.history.pushState({ safeWallet: true, screen, layer: 'change-pin' } satisfies HistoryState, '');
  }

  function openEditor(mode: NonNullable<EditorMode>) {
    setChooserOpen(false); setEditor(mode);
    window.history.pushState({ safeWallet: true, screen, layer: 'editor' } satisfies HistoryState, '');
  }

  function openItem(item: MaterializedRecord<MemoryCard>) {
    if (item.conflicted) {
      setConflictItem(item);
      window.history.pushState({ safeWallet: true, screen, layer: 'conflict' } satisfies HistoryState, '');
      return;
    }
    setViewer(item);
    window.history.pushState({ safeWallet: true, screen, layer: 'viewer' } satisfies HistoryState, '');
  }

  function openConflict(item: MaterializedRecord<RecordPayload>) {
    setConflictItem(item);
    window.history.pushState({ safeWallet: true, screen, layer: 'conflict' } satisfies HistoryState, '');
  }

  function editViewedItem() {
    if (!viewer) return;
    setEditor({ kind: viewer.payload.presentationKind as Exclude<PresentationKind, 'general'>, item: viewer });
    setViewer(null);
    window.history.replaceState({ safeWallet: true, screen, layer: 'editor' } satisfies HistoryState, '');
  }

  function closeLayer() {
    if (window.history.state?.safeWallet) window.history.back();
    else { setEditor(null); setViewer(null); setConflictItem(null); setChooserOpen(false); setCollectionPicker(null); }
  }

  async function saveCard(card: MemoryCard, baseRevisionId: string | null, removedAttachmentIds: string[] = []) {
    if (!repository || !vaultKeyRef.current) return;
    setSaving(true);
    const sourceRevisionId = await repository.savePayload(card, 'card', vaultKeyRef.current, baseRevisionId);
    await cancelAttachmentDeletions(card.attachmentRefs.map((ref) => ref.attachmentId));
    if (removedAttachmentIds.length > 0) {
      await queueAttachmentDeletions(removedAttachmentIds, card.recordId, sourceRevisionId);
    }
    if (query.trim() && !baseRevisionId) setQuery('');
    setEditor(null);
    window.history.back();
    await refresh();
    void processAttachmentSyncQueue(true).catch((cause) => console.warn('Attachment sync deferred.', cause));
  }

  async function deleteCard(item: MaterializedRecord<MemoryCard>) {
    if (!repository || !vaultKeyRef.current || !confirm(t('item.confirmDelete', { title: item.payload.title }))) return;
    const sourceRevisionId = await repository.savePayload(item.payload, 'card', vaultKeyRef.current, item.revisionId, { deleted: true });
    const attachmentIds = item.payload.attachmentRefs.map((ref) => ref.attachmentId);
    await queueAttachmentDeletions(attachmentIds, item.recordId, sourceRevisionId);
    setEditor(null);
    window.history.back();
    await refresh();
    void processAttachmentSyncQueue(true).catch((cause) => console.warn('Attachment deletion deferred.', cause));
  }

  async function togglePinned(item: MaterializedRecord<MemoryCard>) {
    if (!repository || !vaultKeyRef.current) return;
    const updated: MemoryCard = { ...item.payload, pinned: !item.payload.pinned, clientMetadata: { ...item.payload.clientMetadata, updatedAt: nowIso() } };
    await repository.savePayload(updated, 'card', vaultKeyRef.current, item.revisionId);
    await refresh();
  }

  // Marking a reminder done keeps its date and only records when it was completed, so it
  // drops off Upcoming without losing anything. Unticking is a plain undo, so it does not ask.
  async function toggleReminderCompleted(item: MaterializedRecord<MemoryCard>, completed: boolean) {
    if (!repository || !vaultKeyRef.current || !item.payload.reminder) return;
    if (completed && !confirm(t('reminder.confirmDone'))) return;
    const { completedAt: _previous, ...reminder } = item.payload.reminder;
    const updated: MemoryCard = {
      ...item.payload,
      reminder: completed ? { ...reminder, completedAt: nowIso() } : reminder,
      clientMetadata: { ...item.payload.clientMetadata, updatedAt: nowIso() },
    };
    const revisionId = await repository.savePayload(updated, 'card', vaultKeyRef.current, item.revisionId);
    // Keep the open card on the new revision so a later Edit does not save against a stale one.
    setViewer((current) => current && current.recordId === item.recordId ? { ...current, payload: updated, revisionId } : current);
    await refresh();
  }

  async function createCollection(event: FormEvent) {
    event.preventDefault();
    if (!repository || !vaultKeyRef.current || !newCollectionName.trim()) return;
    const timestamp = nowIso();
    const collection: CollectionRecord = { schemaVersion: 1, recordId: crypto.randomUUID(), name: newCollectionName.trim(), createdAt: timestamp, updatedAt: timestamp };
    await repository.savePayload(collection, 'collection', vaultKeyRef.current, null);
    setNewCollectionName('');
    await refresh();
  }

  async function deleteCollection(item: MaterializedRecord<CollectionRecord>) {
    if (!repository || !vaultKeyRef.current || !confirm(t('collections.confirmDelete', { name: item.payload.name }))) return;
    await repository.savePayload(item.payload, 'collection', vaultKeyRef.current, item.revisionId, { deleted: true });
    const affected = snapshot.cards.filter((card) => card.payload.collectionIds.includes(item.recordId));
    for (const card of affected) {
      const updated = { ...card.payload, collectionIds: card.payload.collectionIds.filter((id) => id !== item.recordId), clientMetadata: { ...card.payload.clientMetadata, updatedAt: nowIso() } };
      await repository.savePayload(updated, 'card', vaultKeyRef.current, card.revisionId);
    }
    await refresh();
  }

  async function changePin(currentPin: string, newPin: string, accountPassword: string) {
    if (!device) throw new Error(t('pin.errDeviceUnavailable'));
    const secret = await loadDeviceSecret(device);
    let verifiedVaultKey: Uint8Array | null = null;
    try {
      verifiedVaultKey = await unlockVaultRootKey(currentPin, secret, device.pinEnvelope);

      // For a synced wallet, update the server copy first so a failure leaves nothing
      // half-changed. This copy is what a new device uses, and it is locked by the
      // account password together with the PIN — so both are needed to re-lock it.
      if (device.accountUserId) {
        if (!supabase) throw new Error(t('pin.errNoSync'));
        if (!device.accountEmail) throw new Error(t('pin.errNoEmail'));
        if (!navigator.onLine) throw new Error(t('pin.errOffline'));
        const { error: signInError } = await supabase.auth.signInWithPassword({ email: device.accountEmail, password: accountPassword });
        if (signInError) throw new Error(t('pin.errWrongPassword'));
        const keyPackage = await createAccountWalletKeyPackage(accountPassword, newPin, verifiedVaultKey, device.vaultId);
        const { error: packageError } = await supabase.rpc('upsert_vault_key_package', {
          p_vault_id: device.vaultId,
          p_kdf: keyPackage.kdf,
          p_nonce: keyPackage.wrappedVaultRootKey.nonce,
          p_ciphertext: keyPackage.wrappedVaultRootKey.ciphertext,
        });
        if (packageError) throw packageError;
      }

      const pinEnvelope = await createPinEnvelope(newPin, secret, verifiedVaultKey);
      const updated: DeviceState = { ...device, pinEnvelope, updatedAt: nowIso() };
      await saveDeviceState(updated);
      setDevice(updated);
      setPinMessage(device.accountUserId
        ? t('pin.changedBoth')
        : t('pin.changedLocal'));
    } finally {
      await clearBytes(verifiedVaultKey);
      await clearBytes(secret);
    }
  }

  async function updateSettings(next: AppSettings) {
    setSettings(next);
    await saveSettings(next);
  }

  async function resetLocalWallet() {
    if (!database || !device) return;
    const removingVaultId = device.vaultId;
    const removingLabel = walletProfileTitle(t, device);
    const others = (await loadDeviceStates()).filter((profile) => profile.vaultId !== removingVaultId);

    const message = others.length > 0
      ? t('reset.confirmOther', { name: removingLabel })
      : t('reset.confirmLast');
    if (!confirm(message)) return;

    await lock();
    await disconnectCloudSync(database).catch(() => undefined);
    await clearPendingPairing();

    if (others.length === 0) {
      // Last wallet on this browser: safe to clear the shared local store and the account session.
      await database.disconnectAndClear({ clearLocal: true }).catch(() => undefined);
      await supabase?.auth.signOut().catch(() => undefined);
      await deleteDeviceState();
      const profiles = await loadDeviceStates();
      setSavedDevices(profiles);
      setWalletChosen(profiles.length <= 1);
      setDevice(null); setRepository(null); setLoading(false);
      return;
    }

    // Other wallets remain: never wipe the shared local database or the browser account
    // session. Remove only this wallet's device profile so it can no longer be unlocked or
    // decrypted here; its encrypted rows stay inert without the deleted device secret and
    // are cleared by a later full reset once it is the only wallet left.
    await deleteDeviceState(removingVaultId);
    setRepository(null);
    if (others.length === 1) {
      const remaining = await switchDeviceState(others[0].vaultId);
      setSavedDevices([remaining]);
      setDevice(remaining);
      setWalletChosen(true);
    } else {
      setSavedDevices(await loadDeviceStates());
      setDevice(null);
      setWalletChosen(false);
    }
    setSwitchingWallet(false);
    setLoading(false);
  }

  const collectionMap = useMemo(() => new Map(snapshot.collections.map((entry) => [entry.recordId, entry.payload])), [snapshot.collections]);
  const sortedCards = useMemo(() => [...snapshot.cards].sort((a, b) => b.payload.clientMetadata.updatedAt.localeCompare(a.payload.clientMetadata.updatedAt)), [snapshot.cards]);
  const filteredCards = useMemo(() => {
    const matched = new Set(searchCards(sortedCards.map((entry) => entry.payload), query, collectionMap).map((card) => card.recordId));
    return sortedCards.filter((entry) => matched.has(entry.recordId));
  }, [collectionMap, query, sortedCards]);
  const pinnedCards = useMemo(() => sortedCards.filter((entry) => entry.payload.pinned), [sortedCards]);
  const reminders = useMemo(() => sortedCards.filter((entry) => entry.payload.reminder).sort((a, b) => reminderTime(a.payload) - reminderTime(b.payload)), [sortedCards]);
  const upcoming = useMemo(() => reminders.filter((entry) => !entry.payload.reminder?.completedAt && reminderTime(entry.payload) >= Date.now() - 86_400_000).slice(0, 3), [reminders]);
  const activeCollection = snapshot.collections.find((entry) => entry.recordId === activeCollectionId);
  const visibleCards = useMemo(() => {
    if (query.trim()) return filteredCards;
    if (activeCollectionId) return sortedCards.filter((entry) => entry.payload.collectionIds.includes(activeCollectionId));
    if (screen === 'pinned') return pinnedCards;
    if (screen === 'reminders') return reminders;
    if (screen === 'note' || screen === 'password' || screen === 'card' || screen === 'photoId') return sortedCards.filter((entry) => entry.payload.presentationKind === screen);
    return sortedCards;
  }, [activeCollectionId, filteredCards, pinnedCards, query, reminders, screen, sortedCards]);

  if (passwordRecovery) return <PasswordRecoveryScreen onDone={() => setPasswordRecovery(false)} />;
  if (walletRecovery && walletRecoveryEnabled) return <WalletRecoveryScreen onCancel={() => setWalletRecovery(false)} onRecovered={async (recoveredDevice, key) => { setWalletRecovery(false); await enterOpenState(recoveredDevice, key); }} />;
  if (loading) return <main className="loading-screen">{t('status.openingDatabase')}</main>;
  if (error && !device) return <GateShell><p className="brand">Safe Wallet</p><h1>{t('status.couldNotStart')}</h1><p className="form-error">{error}</p></GateShell>;
  if (switchingWallet || (!unlocked && savedDevices.length > 1 && !walletChosen)) return <WalletChooser profiles={savedDevices} currentVaultId={device?.vaultId} showBack={switchingWallet} onBack={() => { setSwitchingWallet(false); setWalletChosen(Boolean(device)); }} onAdd={() => { setOpenExistingEmail(null); setDevice(null); setWalletChosen(true); setSwitchingWallet(false); }} onSelect={async (profile) => { const selected = await switchDeviceState(profile.vaultId); setDevice(selected); setWalletChosen(true); setSwitchingWallet(false); }} />;
  if (!device) return <InitialSetup onReady={enterOpenState} onRecover={() => setWalletRecovery(true)} initialMode={openExistingEmail ? 'existing' : undefined} initialEmail={openExistingEmail ?? undefined} />;
  if (!unlocked || !settings || !database || !repository || !vaultKeyRef.current) return <UnlockScreen device={device} walletCount={savedDevices.length || 1} onUseAnother={() => { setOpenExistingEmail(null); if (savedDevices.length > 1) { setWalletChosen(false); setSwitchingWallet(true); } else { setDevice(null); setWalletChosen(true); } }} onUnlock={unlock} onRecover={() => setWalletRecovery(true)} />;

  const typeDisplay = settings.categoryLabelMode;
  return (
    <main className={`app-shell spacing-${settings.rowSpacing}`}>
      <header className="app-header">
        <div className="header-primary-actions">
          <button className={`home-button ${screen === 'home' && !activeCollectionId ? 'active' : ''}`} type="button" onClick={() => navigate('home')} aria-label={t('menu.home')} title={t('menu.home')}><HomeIcon /></button>
          <div className="menu-anchor">
            <button className="wallet-menu-button" type="button" onClick={() => setMenuOpen((value) => !value)}>Safe Wallet <span className="menu-chevron" /></button>
            {menuOpen && <><button className="menu-backdrop" aria-label={t('menu.closeMenu')} type="button" onClick={() => setMenuOpen(false)} /><nav className="wallet-menu">
              <button className={screen === 'home' ? 'active' : ''} type="button" onClick={() => navigate('home')}>{t('menu.home')}</button>
              {MENU_ENTRIES.map((entry) => <button key={entry.screen} className={screen === entry.screen ? 'active' : ''} type="button" onClick={() => navigate(entry.screen)}>{t(entry.labelKey)}</button>)}
              <div className="menu-divider" />
              <button className={screen === 'collections' ? 'active' : ''} type="button" onClick={() => navigate('collections')}>{t('menu.collections')}</button>
              {snapshot.conflictRecords.length > 0 && <button className={screen === 'conflicts' ? 'active' : ''} type="button" onClick={() => navigate('conflicts')}>{t('menu.conflicts', { count: snapshot.conflictRecords.length })}</button>}
              <button className={screen === 'settings' ? 'active' : ''} type="button" onClick={() => navigate('settings')}>{t('menu.settings')}</button>
              <div className="menu-divider" />
              <label className="menu-language">
                <span>{t('menu.language')}</span>
                <select value={locale} onChange={(event) => setLocale(event.target.value as Locale)}>
                  {LOCALES.map((entry) => <option key={entry.code} value={entry.code}>{entry.label}</option>)}
                </select>
              </label>
              <div className="menu-divider" />
              <button className="menu-lock-button" type="button" onClick={() => void lock()}>{t('menu.lock')}</button>
            </nav></>}
          </div>
        </div>
        <HeaderStatus saving={saving} value={saving ? t('status.saving') : `${syncStatusText} — ${plural('items', snapshot.cards.length)}`} />
      </header>

      <span className="app-version-footer">v{APP_VERSION}</span>

      {mergeNotice && <div className="merge-notice" role="status">
        <span>{mergeNotice}</span>
        <div className="merge-notice-actions">
          {mergedFrom && <button className="secondary-button compact" type="button" disabled={!mergedBackupReady} onClick={() => void removeMergedSource()}>{mergedBackupReady ? t('merge.removeOld') : t('merge.backingUp')}</button>}
          <button className="text-button" type="button" onClick={() => { setMergeNotice(null); setMergedFrom(null); setMergedBackupReady(false); }}>{t('merge.dismiss')}</button>
        </div>
      </div>}

      {screen !== 'settings' && screen !== 'collections' && <div className="search-row"><label className="search-field"><span className="sr-only">{t('search.placeholder')}</span><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder={t('search.placeholder')} /></label><button className="add-button" type="button" onClick={openChooser}>{t('action.addNew')}</button></div>}

      {screen === 'settings' ? <section className="settings-screen">
        <div className="screen-title"><h1>{t('settings.title')}</h1><button className="text-button" type="button" onClick={() => navigate('home')}>{t('action.back')}</button></div>
        <SettingsSection title={t('settings.account')} status={device.accountEmail ? t('account.signedIn') : t('settings.notSignedIn')} defaultOpen>
          <AccountPanel device={device} database={database} vaultKey={vaultKeyRef.current} onDeviceUpdated={(updated) => { setDevice(updated); void loadDeviceStates().then(setSavedDevices); }} onMergeIntoAccount={mergeIntoAccount} />
        </SettingsSection>

        <SettingsSection title={t('settings.security')} status={device.helloCredentialId ? t('settings.pinAndFingerprint') : t('settings.pinOnly')}>
          <div className="setting-action-row"><p>{t('settings.changePinRow')}</p><button className="secondary-button compact" type="button" onClick={openChangePin}>{t('pin.change')}</button></div>
          {pinMessage && <p className="status-message" role="status">{pinMessage}</p>}
          <FingerprintUnlockRow device={device} onDeviceUpdated={(updated) => { setDevice(updated); void loadDeviceStates().then(setSavedDevices); }} />
        </SettingsSection>

        {device.accountUserId && walletRecoveryEnabled && <SettingsSection title={t('settings.recovery')}>
          <RecoveryProtectionPanel device={device} vaultRootKey={vaultKeyRef.current} />
        </SettingsSection>}

        <SettingsSection title={t('settings.thisDevice')} status={device.deviceName}>
          <div className="device-identity"><strong>{walletProfileTitle(t, device)}</strong><span>{walletProfileSubtitle(t, device, locale)}</span><small>{device.deviceId}</small></div>
          <div className="button-row">
            <button className="secondary-button compact" type="button" onClick={() => void lock()}>{t('settings.lockNow')}</button>
            <button className="secondary-button compact" type="button" onClick={() => { void lock().then(() => { if (savedDevices.length > 1) { setWalletChosen(false); setSwitchingWallet(true); } else { setDevice(null); setWalletChosen(true); } }); }}>{savedDevices.length > 1 ? t('settings.chooseAnotherWallet') : t('settings.useAnotherAccount')}</button>
          </div>
        </SettingsSection>

        <SettingsSection title={t('settings.appearance')}>
          <label className="setting-row setting-select-row"><span className="setting-key">{t('settings.categoryLabels')}</span><select className="setting-select" value={settings.categoryLabelMode} onChange={(event) => void updateSettings({ ...settings, categoryLabelMode: event.target.value as AppSettings['categoryLabelMode'] })}><option value="name">{t('settings.optionName')}</option><option value="icon">{t('settings.optionIcon')}</option><option value="both">{t('settings.optionBoth')}</option></select></label>
          <label className="setting-row setting-select-row"><span className="setting-key">{t('settings.rowSpacing')}</span><select className="setting-select" value={settings.rowSpacing} onChange={(event) => void updateSettings({ ...settings, rowSpacing: event.target.value as AppSettings['rowSpacing'] })}><option value="large">{t('settings.optionLarge')}</option><option value="medium">{t('settings.optionMedium')}</option><option value="compact">{t('settings.optionCompact')}</option></select></label>
          <label className="setting-row setting-select-row"><span className="setting-key">{t('settings.textSize')}</span><select className="setting-select" value={settings.fontScale ?? 'medium'} onChange={(event) => void updateSettings({ ...settings, fontScale: event.target.value as AppSettings['fontScale'] })}><option value="small">{t('settings.optionSmall')}</option><option value="medium">{t('settings.optionMedium')}</option><option value="large">{t('settings.optionLarge')}</option></select></label>
          <label className="setting-row setting-select-row"><span className="setting-key">{t('settings.autoLock')}</span><select className="setting-select" value={settings.autoLockMinutes} onChange={(event) => void updateSettings({ ...settings, autoLockMinutes: Number(event.target.value) })}>{[1, 5, 15, 30].map((minutes) => <option key={minutes} value={minutes}>{plural('minutes', minutes)}</option>)}</select></label>
        </SettingsSection>

        <SettingsSection title={t('settings.searchPrivacy')}>
          <p>{t('settings.searchPrivacyNote')}</p>
          <details><summary>{t('settings.exampleProjection')}</summary><pre>{snapshot.cards[0] ? JSON.stringify(buildSearchProjection(snapshot.cards[0].payload, collectionMap), null, 2) : t('settings.noItemsYet')}</pre></details>
        </SettingsSection>

        <SettingsSection title={t('settings.dangerZone')}>
          <p>{t('settings.removeWalletNote')}</p>
          <button className="danger-link" type="button" onClick={() => void resetLocalWallet()}>{t('settings.removeWallet')}</button>
        </SettingsSection>
      </section> : screen === 'collections' ? <section className="collections-screen">
        <div className="screen-title"><h1>{t('menu.collections')}</h1><button className="text-button" type="button" onClick={() => navigate('home')}>{t('action.back')}</button></div>
        <form className="new-collection" onSubmit={createCollection}><input value={newCollectionName} onChange={(event) => setNewCollectionName(event.target.value)} placeholder={t('collections.newName')} /><button className="secondary-button" disabled={!newCollectionName.trim()} type="submit">{t('action.add')}</button></form>
        <div className="collection-list">{snapshot.collections.map((entry) => <article className="collection-row" key={entry.recordId}><button type="button" onClick={() => navigate('home', entry.recordId)}><strong>{entry.payload.name}</strong><small>{snapshot.cards.filter((card) => card.payload.collectionIds.includes(entry.recordId)).length} items</small></button><button className="danger-icon" type="button" aria-label={`Delete ${entry.payload.name}`} onClick={() => void deleteCollection(entry)}>×</button></article>)}</div>
        {snapshot.collections.length === 0 && <p className="small-empty">{t('collections.note')}</p>}
      </section> : <section className="home-content">
        {query.trim() ? <><SectionHeading title={t('section.searchResults')}/><div className="item-list">{filteredCards.map((item) => <ItemRow key={item.recordId} item={item} labelMode={typeDisplay} showType onOpen={() => openItem(item)} onTogglePinned={() => void togglePinned(item)} />)}</div>{filteredCards.length === 0 && <p className="small-empty">{t('empty.noResults')}</p>}</> : activeCollection ? <><div className="screen-title"><h1>{activeCollection.payload.name}</h1><button className="text-button" type="button" onClick={() => navigate('collections')}>{t('menu.collections')}</button></div><div className="item-list">{visibleCards.map((item) => <ItemRow key={item.recordId} item={item} labelMode={typeDisplay} showType onOpen={() => openItem(item)} onTogglePinned={() => void togglePinned(item)} />)}</div></> : screen === 'home' ? <div className="home-sections">
          {device.accountUserId && walletRecoveryEnabled && <><RecoveryApprovalInbox device={device} vaultRootKey={vaultKeyRef.current} /><RecoveryInvitationInbox device={device} vaultRootKey={vaultKeyRef.current} /></>}
          {upcoming.length > 0 && <section className="content-section"><SectionHeading title={t('section.upcoming')} onMore={reminders.length > 3 ? () => navigate('reminders') : undefined}/><div className="item-list">{upcoming.map((item) => <ItemRow key={item.recordId} item={item} labelMode={typeDisplay} showType onOpen={() => openItem(item)} onTogglePinned={() => void togglePinned(item)} />)}</div></section>}
          {pinnedCards.length > 0 && <section className="content-section"><SectionHeading title={t('section.pinned')} onMore={pinnedCards.length > 5 ? () => navigate('pinned') : undefined}/><div className="item-list">{pinnedCards.slice(0, 5).map((item) => <ItemRow key={item.recordId} item={item} labelMode={typeDisplay} showType onOpen={() => openItem(item)} onTogglePinned={() => void togglePinned(item)} />)}</div></section>}
          <section className="content-section"><SectionHeading title={t('section.recent')}/><div className="item-list">{sortedCards.map((item) => <ItemRow key={item.recordId} item={item} labelMode={typeDisplay} showType onOpen={() => openItem(item)} onTogglePinned={() => void togglePinned(item)} />)}</div>{sortedCards.length === 0 && <p className="small-empty">{t('empty.addFirst')}</p>}</section>
        </div> : screen === 'conflicts' ? <><div className="screen-title"><h1>{t('section.conflicts')}</h1><button className="text-button" type="button" onClick={() => navigate('home')}>{t('action.back')}</button></div><p className="feature-note">{t('conflict.screenNote')}</p><div className="item-list">{snapshot.conflictRecords.map((item) => <ConflictRow key={item.recordId} item={item} onOpen={() => openConflict(item)} />)}</div>{snapshot.conflictRecords.length === 0 && <p className="small-empty">{t('conflict.none')}</p>}</> : <><div className="screen-title"><h1>{screen === 'pinned' ? t('section.pinned') : screen === 'reminders' ? t('menu.reminders') : t(TYPE_PLURAL_KEYS[screen as Exclude<PresentationKind, 'general'>])}</h1><button className="text-button" type="button" onClick={() => navigate('home')}>{t('action.back')}</button></div><div className="item-list">{visibleCards.map((item) => <ItemRow key={item.recordId} item={item} labelMode={typeDisplay} showType={screen === 'pinned' || screen === 'reminders'} onOpen={() => openItem(item)} onTogglePinned={() => void togglePinned(item)} />)}</div>{visibleCards.length === 0 && <p className="small-empty">{t('empty.nothingHere')}</p>}</>}
      </section>}

      {changePinOpen && <ChangePinDialog onChange={changePin} onClose={closeLayer} requireAccountPassword={Boolean(device.accountUserId)} />}
      {viewer && vaultKeyRef.current && <ItemViewer item={viewer} collections={snapshot.collections} vaultKey={vaultKeyRef.current} onClose={closeLayer} onEdit={editViewedItem} onToggleReminderCompleted={(completed) => void toggleReminderCompleted(viewer, completed)} />}
      {chooserOpen && <AddChooser onClose={closeLayer} onChoose={(kind) => openEditor({ kind, initialTitle: query.trim() || undefined })} />}
      {editor && vaultKeyRef.current && <ItemEditor mode={editor} collections={snapshot.collections} vaultKey={vaultKeyRef.current} onSave={saveCard} onDelete={deleteCard} onClose={closeLayer} onOpenCollectionPicker={(selected, callback) => { setCollectionPicker({ selected, callback }); window.history.pushState({ safeWallet: true, screen, layer: 'collection-picker' } satisfies HistoryState, ''); }} />}
      {conflictItem && <ConflictResolver item={conflictItem} repository={repository} vaultKey={vaultKeyRef.current} onClose={closeLayer} onResolved={async () => { setConflictItem(null); window.history.back(); await refresh(); }} />}
      {collectionPicker && <CollectionPicker collections={snapshot.collections} selected={collectionPicker.selected} onClose={closeLayer} onSave={(ids) => { collectionPicker.callback(ids); closeLayer(); }} />}
    </main>
  );
}
